import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useAuthState } from 'react-firebase-hooks/auth';
import { updateProfile } from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { theme } from '../styles/GlobalStyles';
import Input from '../components/Input';
import NestTestModal from '../components/NestTestModal';
import TasteTestModal from '../components/TasteTestModal';
import MessTestModal from '../components/MessTestModal';

// Styled Components
const ProfileContainer = styled.div`
  position: fixed;
  top: 80px;
  left: 220px;
  width: calc(100vw - 220px);
  height: calc(100vh - 80px);
  padding: ${theme.spacing.md};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  overflow-y: auto;
  z-index: 1;
  transition: all 0.3s ease;
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;
  scrollbar-width: none;
  
  @media (max-width: 1200px) {
    left: 200px;
    width: calc(100vw - 200px);
    padding: ${theme.spacing.sm};
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    left: 180px;
    width: calc(100vw - 180px);
    padding: ${theme.spacing.sm};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    top: 70px;
    left: 0;
    width: 100vw;
    height: calc(100vh - 70px);
    padding: 0;
    background: ${theme.colors.background};
    /* Account for mobile browser UI */
    height: calc(100dvh - 70px); /* Dynamic viewport height for mobile browsers */
  }
`;

const ProfileCard = styled.div`
  background: white;
  border-radius: 24px;
  padding: 0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid ${theme.colors.primaryLight}20;
  width: min(98%, 1600px);
  height: min(95%, 900px);
  max-width: none;
  max-height: none;
  overflow: hidden;
  display: flex;
  flex-direction: row-reverse;
  position: relative;
  gap: 0;
  transition: all 0.3s ease;
  
  @media (max-width: 1400px) {
    width: min(96%, 1400px);
    height: min(93%, 800px);
  }
  
  @media (max-width: 1200px) {
    width: min(94%, 1200px);
    height: min(91%, 700px);
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: min(96%, 800px);
    height: min(96%, 500px);
    flex-direction: column;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    background: transparent;
    border-radius: 0;
    padding: 0;
    box-shadow: none;
    border: none;
    width: 100%;
    height: 100%;
    flex-direction: column;
    overflow: visible;
  }
`;

const EditButton = styled.button<{ isActive?: boolean }>`
  position: absolute;
  top: ${theme.spacing.md};
  right: ${theme.spacing.md};
  background: none;
  border: none;
  color: ${props => props.isActive ? theme.colors.primary : '#9ca3af'};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  padding: ${theme.spacing.sm};
  border-radius: 8px;
  transition: all 0.2s ease;
  min-height: ${theme.mobile.minTouchTarget};
  min-width: ${theme.mobile.minTouchTarget};
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: ${theme.colors.primary};
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    top: ${theme.spacing.sm};
    right: ${theme.spacing.sm};
    padding: ${theme.spacing.sm};
    min-height: 44px;
    min-width: 44px;
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const SaveButton = styled.button`
  position: absolute;
  top: ${theme.spacing.md};
  right: ${theme.spacing.md};
  background: none;
  border: none;
  color: ${theme.colors.primary};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  padding: ${theme.spacing.sm};
  border-radius: 8px;
  transition: all 0.2s ease;
  min-height: ${theme.mobile.minTouchTarget};
  min-width: ${theme.mobile.minTouchTarget};
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: ${theme.colors.secondary};
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    top: ${theme.spacing.sm};
    right: ${theme.spacing.sm};
    padding: ${theme.spacing.sm};
    min-height: 44px;
    min-width: 44px;
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const ResetButton = styled.button`
  position: absolute;
  top: ${theme.spacing.md};
  left: ${theme.spacing.md};
  background: none;
  border: none;
  color: ${theme.colors.error};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  padding: ${theme.spacing.sm};
  border-radius: 8px;
  transition: all 0.2s ease;
  min-height: ${theme.mobile.minTouchTarget};
  min-width: ${theme.mobile.minTouchTarget};
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #dc3545;
    transform: scale(1.1);
    background: rgba(220, 53, 69, 0.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    top: ${theme.spacing.sm};
    left: ${theme.spacing.sm};
    padding: ${theme.spacing.sm};
    min-height: 44px;
    min-width: 44px;
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const AvatarSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const TestButtonsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  width: 100%;
  align-items: center;
  position: relative;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    &:hover .carousel-nav {
      opacity: 1;
    }
  }
`;

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    gap: 0;
  }
`;

const CarouselButton = styled.button`
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid ${theme.colors.primaryLight}30;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: ${theme.colors.primary};
  font-size: 16px;
  font-weight: normal;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
  opacity: 0;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(255, 255, 255, 1);
    color: ${theme.colors.secondary};
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    display: none;
  }
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    display: none;
  }
`;

const TestButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  align-items: center;
  width: 100%;
  padding: ${theme.spacing.sm} 0;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    flex-direction: row;
    overflow-x: auto;
    overflow-y: visible;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
    scroll-behavior: smooth;
    justify-content: flex-start;
    position: relative;
    
    &::-webkit-scrollbar {
      display: none;
    }
    
    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 30px;
      height: 100%;
      background: linear-gradient(to left, #f8f9fa, transparent);
      pointer-events: none;
      z-index: 1;
    }
  }
`;

const TestTile = styled.button<{ bgColor: string; textColor: string }>`
  background: linear-gradient(135deg, ${props => props.bgColor}, ${props => props.bgColor}dd);
  border: 1px solid ${props => props.bgColor};
  border-radius: 12px;
  padding: ${theme.spacing.sm};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px ${props => props.bgColor}30;
  width: 100%;
  height: 90px;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px ${props => props.bgColor}40;
    background: linear-gradient(135deg, ${props => props.bgColor}ee, ${props => props.bgColor});
  }
  
  &:active {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${props => props.bgColor}30;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 240px;
    min-width: 240px;
    height: 80px;
    padding: ${theme.spacing.xs};
    flex-shrink: 0;
    scroll-snap-align: center;
  }
`;

const TileContent = styled.div<{ textColor: string }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  width: 100%;
  height: 100%;
  
  .icon-container {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: white;
    
    @media (max-width: ${theme.breakpoints.mobile}) {
      width: 32px;
      height: 32px;
    }
    
    svg {
      width: 18px;
      height: 18px;
      
      @media (max-width: ${theme.breakpoints.mobile}) {
        width: 16px;
        height: 16px;
      }
    }
  }
  
  .text-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing.xs};
    height: 100%;
    justify-content: center;
    min-width: 0;
  }
  
  h3 {
    color: white;
    font-size: 0.85rem;
    font-weight: 600;
    margin: 0;
    font-family: ${theme.fonts.main};
    letter-spacing: -0.01em;
    line-height: 1.1;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    
    @media (max-width: ${theme.breakpoints.mobile}) {
      font-size: 0.8rem;
    }
  }
  
  p {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.65rem;
    line-height: 1.2;
    margin: 0;
    font-weight: 400;
    font-family: ${theme.fonts.accent};
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    width: 100%;
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    
    @media (max-width: ${theme.breakpoints.mobile}) {
      font-size: 0.6rem;
      line-height: 1.1;
      -webkit-line-clamp: 2;
    }
  }
`;

const ProfileSidePanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #f8f9fa;
  border-radius: 20px;
  padding: ${theme.spacing.xl};
  box-shadow: none;
  border: none;
  min-width: 280px;
  max-width: 320px;
  height: calc(100% - 20px);
  flex-shrink: 0;
  transition: all 0.3s ease;
  gap: ${theme.spacing.lg};
  overflow-y: auto;
  position: relative;
  margin: 10px 10px 10px 0;
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;
  scrollbar-width: none;
  
  @media (max-width: 1400px) {
    min-width: 260px;
    max-width: 300px;
    padding: ${theme.spacing.lg};
  }
  
  @media (max-width: 1200px) {
    min-width: 240px;
    max-width: 280px;
    padding: ${theme.spacing.lg};
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-width: auto;
    max-width: none;
    width: 100%;
    padding: ${theme.spacing.lg};
    flex-shrink: 1;
    height: auto;
    overflow-y: visible;
    margin: 10px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    min-width: auto;
    width: 100%;
    padding: ${theme.spacing.lg};
    background: #f8f9fa;
    border-radius: 20px;
    box-shadow: none;
    border: none;
    height: auto;
    overflow-y: visible;
    margin: 10px;
  }
`;

const AvatarContainer = styled.div`
  position: relative;
  margin-bottom: ${theme.spacing.sm};
`;

const EditIconButton = styled.button<{ isEditing: boolean }>`
  position: absolute;
  top: -15px;
  right: -15px;
  width: 16px;
  height: 16px;
  padding: 0;
  margin: 0;
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  display: ${props => props.isEditing ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 2;
  
  &:hover {
    transform: scale(1.2);
  }
  
  &:active {
    transform: scale(0.9);
  }
`;

const NameEditButton = styled.button<{ isEditing: boolean }>`
  position: absolute;
  top: -15x;
  right: -35px;
  width: 10px;
  height: 10px;
  padding: 0;
  margin: 0;
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  display: ${props => props.isEditing ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 2;
  
  &:hover {
    transform: scale(1.2);
  }
  
  &:active {
    transform: scale(0.9);
  }
`;

const NameEditingButton = styled.button`
  position: absolute;
  top: -10px;
  right: -8px;
  width: 14px;
  height: 14px;
  padding: 0;
  margin: 0;
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  
  &:hover {
    transform: scale(1.2);
  }
  
  &:active {
    transform: scale(0.9);
  }
`;

const EditIconImage = styled.img`
  width: 14px;
  height: 14px;
  filter: brightness(0) saturate(100%) invert(91%) sepia(6%) saturate(346%) hue-rotate(169deg) brightness(96%) contrast(93%);
  transition: all 0.2s ease;
  
  &:hover {
    filter: brightness(0) saturate(100%) invert(91%) sepia(6%) saturate(346%) hue-rotate(169deg) brightness(96%) contrast(93%);
  }
`;

const NameEditIconImage = styled.img`
  width: 14px;
  height: 14px;
  filter: brightness(0) saturate(100%) invert(91%) sepia(6%) saturate(346%) hue-rotate(169deg) brightness(96%) contrast(93%);
  transition: all 0.2s ease;
  
  &:hover {
    filter: brightness(0) saturate(100%) invert(91%) sepia(6%) saturate(346%) hue-rotate(169deg) brightness(96%) contrast(93%);
  }
`;

const PreferenceEditButton = styled.button<{ isVisible: boolean; isEditing?: boolean }>`
  position: relative;
  width: 16px;
  height: 16px;
  padding: 0;
  margin: 0;
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  display: ${props => props.isVisible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 2;
  
  &:hover {
    transform: scale(1.2);
  }
  
  &:active {
    transform: scale(0.9);
  }
`;

const PreferenceEditIconImage = styled.img`
  width: 14px;
  height: 14px;
  filter: brightness(0) saturate(100%) invert(91%) sepia(6%) saturate(346%) hue-rotate(169deg) brightness(96%) contrast(93%);
  transition: all 0.2s ease;
  
  &:hover {
    filter: brightness(0) saturate(100%) invert(91%) sepia(6%) saturate(346%) hue-rotate(169deg) brightness(96%) contrast(93%);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.xs};
`;

const Avatar = styled.div<{ backgroundImage?: string; gradient?: string }>`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: ${props => {
    if (props.backgroundImage) return `url(${props.backgroundImage})`;
    if (props.gradient) return props.gradient;
    return theme.colors.primaryLight;
  }};
  background-size: cover;
  background-position: center;
  border: 4px solid white;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  color: white;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 100px;
    height: 100px;
    font-size: 2rem;
  }
`;

const GradientOptions = styled.div<{ isEditing: boolean }>`
  display: ${props => props.isEditing ? 'grid' : 'none'};
  grid-template-columns: repeat(6, 20px);
  gap: 6px;
  margin-bottom: ${theme.spacing.md};
  width: fit-content;
  max-width: 100%;
  padding: ${theme.spacing.xs};
  overflow: visible;
  justify-items: center;
  justify-content: center;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: repeat(4, 20px);
    gap: 6px;
    padding: ${theme.spacing.xs};
  }
`;

const GradientOption = styled.button<{ gradient: string; isSelected: boolean }>`
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  max-width: 20px;
  max-height: 20px;
  aspect-ratio: 1;
  border-radius: 50%;
  background: ${props => props.gradient};
  border: none;
  outline: ${props => props.isSelected ? `2px solid ${theme.colors.primary}` : 'none'};
  outline-offset: 1px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
  box-sizing: border-box;
  padding: 0;
  
  &:hover {
    transform: scale(2);
    z-index: 1;
    position: relative;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }
  
  &:active {
    transform: scale(1.5);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 24px;
    height: 24px;
    min-width: 24px;
    min-height: 24px;
    max-width: 24px;
    max-height: 24px;
    
    &:hover {
      transform: scale(1.3);
    }
  }
`;

const PreferencesSection = styled.div`
  margin-top: 0;
  padding: 0;
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  border: none;
  position: relative;
  width: 100%;
  max-width: 100%;
  transition: all 0.3s ease;
  
  @media (max-width: 1400px) {
    max-width: 100%;
    padding: 0;
  }
  
  @media (max-width: 1200px) {
    max-width: 100%;
    padding: 0;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    max-width: 100%;
    margin-top: 0;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0;
    background: transparent;
    border-radius: 0;
    box-shadow: none;
    border: none;
    margin-top: 0;
    max-width: 100%;
  }
`;

const PreferencesHeader = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.sm};
  position: relative;
  width: 100%;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50%;
    height: 2px;
    background: #f0f0f0;
  }
`;

const PreferencesTitle = styled.h2`
  color: ${theme.colors.deepDark};
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  text-align: left;
`;

const PreferenceSubSection = styled.div`
  margin-bottom: ${theme.spacing.lg};
  width: 100%;
`;

const SubHeader = styled.h3`
  color: ${theme.colors.deepDark};
  font-size: 1.1rem;
  font-weight: 500;
  margin: 0 0 ${theme.spacing.sm} 0;
  text-align: left;
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.xs};
  margin-top: ${theme.spacing.xs};
  align-items: flex-start;
`;

const Tag = styled.span<{ selectedGradient?: string }>`
  display: inline-flex;
  align-items: center;
  background: ${props => props.selectedGradient ? props.selectedGradient : `${theme.colors.primaryLight}40`};
  opacity: ${props => props.selectedGradient ? '0.8' : '1'};
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.65rem;
  font-weight: 500;
  border: 1px solid ${props => props.selectedGradient ? 'transparent' : `${theme.colors.primaryLight}60`};
  position: relative;
  padding-right: calc(8px + 12px); /* Reserve more space for button */
  
  button {
    background: none;
    border: none;
    color: white;
    font-size: 1rem;
    cursor: pointer;
    padding: 0;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.5s ease;
    position: absolute;
    right: -10px;
    top: 50%;
    transform: translateY(-50%);
    
    &:hover {
      background: none;
      opacity: 0.7;
    }
  }
`;

const EmptyState = styled.span`
  color: ${theme.colors.lightText};
  font-style: italic;
  font-size: 0.7rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${theme.spacing.lg};
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr 1fr;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const FieldLabel = styled.label`
  color: ${theme.colors.deepDark};
  font-size: 0.9rem;
  font-weight: 500;
`;

const HouseholdMemberRow = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  align-items: center;
  margin-bottom: ${theme.spacing.sm};
`;

const RemoveButton = styled.button`
  background: ${theme.colors.error};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${theme.colors.secondary};
  }
`;

const AddButton = styled.button`
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${theme.colors.secondary};
  }
`;

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  background: ${theme.colors.background};
  border-radius: 8px;
  border: 1px solid ${theme.colors.primaryLight}20;
  margin-bottom: ${theme.spacing.xs};
`;

const DetailLabel = styled.span`
  color: ${theme.colors.text};
  font-size: 0.9rem;
  font-weight: 500;
  min-width: 120px;
`;

const DetailContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  flex: 1;
`;

const CompactSelect = styled.select`
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid #ddd;
  font-size: 0.85rem;
  background: white;
  min-width: 140px;
`;

const NameDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: ${theme.spacing.sm};
  width: fit-content;
  position: relative;
  margin-left: auto;
  margin-right: auto;
`;

const NameText = styled.h2`
  color: ${theme.colors.deepDark};
  font-size: 1rem;
  font-weight: 500;
  margin: 0;
  text-align: center;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.9rem;
  }
`;

const NameEditInput = styled.input`
  color: ${theme.colors.deepDark};
  font-size: 1rem;
  font-weight: 500;
  margin: 0;
  text-align: center;
  background: transparent;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 4px 8px;
  width: auto;
  min-width: 120px;
  max-width: 250px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(247, 173, 121, 0.2);
  }
  
  &::placeholder {
    color: #aaa;
    font-weight: 300;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.9rem;
  }
`;

const NameEditContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: ${theme.spacing.sm};
  width: fit-content;
  position: relative;
  margin-left: auto;
  margin-right: auto;
`;

const SaveActions = styled.div<{ isEditing: boolean }>`
  display: ${props => props.isEditing ? 'flex' : 'none'};
  gap: ${theme.spacing.md};
  justify-content: center;
  margin-top: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const CenterSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: ${theme.spacing.xl};
  padding: ${theme.spacing.xl};
  min-width: 0;
  transition: all 0.3s ease;
  overflow-y: auto;
  height: 100%;
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;
  scrollbar-width: none;
  
  @media (max-width: 1400px) {
    gap: ${theme.spacing.lg};
    padding: ${theme.spacing.lg};
  }
  
  @media (max-width: 1200px) {
    gap: ${theme.spacing.md};
    padding: ${theme.spacing.md};
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.lg};
    gap: ${theme.spacing.lg};
    height: auto;
    overflow-y: visible;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.lg};
    width: 100%;
    overflow: visible;
    height: auto;
  }
`;

const HouseholdTag = styled.span<{ customColor?: string; isSelected?: boolean; hasRemoveButton?: boolean }>`
  display: inline-flex;
  align-items: center;
  background: ${props => props.customColor || `${theme.colors.primaryLight}`};
  opacity: ${props => props.customColor ? '0.8' : '0.6'};
  color: ${props => props.customColor ? 'white' : theme.colors.deepDark};
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
  border: ${props => props.isSelected ? `2px solid ${theme.colors.primary}` : props.customColor ? 'transparent' : `1px solid ${theme.colors.primaryLight}60`};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  padding-right: ${props => props.hasRemoveButton ? 'calc(8px + 20px)' : '8px'}; /* Reserve space for button only when needed */
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
  
  button {
    background: none;
    border: none;
    color: white;
    font-size: 1rem;
    cursor: pointer;
    padding: 0;
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.2s ease;
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    
    &:hover {
      background: none;
      opacity: 0.7;
    }
  }
`;

const ColorPicker = styled.div<{ isVisible: boolean }>`
  display: ${props => props.isVisible ? 'grid' : 'none'};
  grid-template-columns: repeat(6, 20px);
  gap: 6px;
  margin-top: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
  width: fit-content;
  max-width: 100%;
  padding: ${theme.spacing.xs};
  overflow: visible;
  justify-items: center;
  justify-content: center;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: repeat(4, 20px);
    gap: 6px;
    padding: ${theme.spacing.xs};
  }
`;

const ColorOption = styled.button<{ color: string; isSelected: boolean }>`
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  max-width: 20px;
  max-height: 20px;
  aspect-ratio: 1;
  border-radius: 50%;
  background: ${props => props.color};
  border: none;
  outline: ${props => props.isSelected ? `2px solid ${theme.colors.primary}` : 'none'};
  outline-offset: 1px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
  box-sizing: border-box;
  padding: 0;
  
  &:hover {
    transform: scale(1.3);
    z-index: 1;
    position: relative;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }
  
  &:active {
    transform: scale(1.1);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 24px;
    height: 24px;
    min-width: 24px;
    min-height: 24px;
    max-width: 24px;
    max-height: 24px;
    
    &:hover {
      transform: scale(1.2);
    }
  }
`;

// Gradient options matching the app's color scheme
const gradientOptions = [
  `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.secondary})`,
  `linear-gradient(135deg, ${theme.colors.secondary}, ${theme.colors.tertiary})`,
  `linear-gradient(135deg, ${theme.colors.tertiary}, ${theme.colors.accent})`,
  `linear-gradient(135deg, ${theme.colors.accent}, ${theme.colors.darkAccent})`,
  `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.tertiary})`,
  `linear-gradient(135deg, ${theme.colors.secondary}, ${theme.colors.accent})`,
  `linear-gradient(135deg, ${theme.colors.primaryLight}, ${theme.colors.primary})`,
  `linear-gradient(135deg, ${theme.colors.darkAccent}, ${theme.colors.deepDark})`,
];

// Color options for household member tags
const householdColorOptions = [
  '#E53E3E', // More Red
  '#4ECDC4', // Teal
  '#45B7D1', // Sky Blue
  '#96CEB4', // Sage Green
  '#515a7a', // Dark Purple (from theme)
  '#2C5282', // Navy
  '#FF8B94', // Pink
  '#B4A7D6', // Lavender
  '#F7DC6F', // Golden Yellow
  '#85C1E9', // Light Blue
  '#F8C471', // Peach
  '#82E0AA', // Light Green
];

interface UserProfileData {
  selectedGradient?: string;
  isUsingGradient?: boolean;
  foodPreferences?: string[];
  householdMembers?: { name: string; age: number }[];
  takeawayNight?: string;
  allergies?: string[];
  householdMemberColors?: { [key: number]: string };
  completedTests?: string[];
  nestTestData?: {
    rooms?: { [key: string]: number };
    homeTimes?: { member: string; homeFrom: string; homeTo: string }[];
  };
  tasteTestData?: {
    foodPreferences?: {
      cuisines: string[];
      flavors: string[];
      mealTypes: string[];
      snackTypes: string[];
    };
    dietaryRestrictions?: Array<{
      name: string;
      type: 'allergy' | 'intolerance' | 'preference' | 'lifestyle';
    }>;
    cookingHabits?: {
      frequency: string;
      preferredMealTypes: string[];
      cookingSkillLevel: string;
      timeAvailable: string;
    };
    budgetRange?: string;
    shoppingPreferences?: string[];
  };
  messTestData?: {
    choreAssignments: Array<{
      member: string;
      chores: string[];
    }>;
    noChoresDays: string[];
    productiveDays: string[];
    stepByStepPreference: boolean;
    choreFrequency: { [key: string]: string };
    chorePreferences: {
      preferredTime: string;
      motivationStyle: string;
      choreStyle: string;
    };
  };
}

const Profile: React.FC = () => {
  const [user] = useAuthState(auth);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingAvatar, setIsEditingAvatar] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingPreferences, setIsEditingPreferences] = useState(false);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [profileImage, setProfileImage] = useState<string | null>(user?.photoURL || null);
  const [selectedGradient, setSelectedGradient] = useState<string>(gradientOptions[0]);
  const [isUsingGradient, setIsUsingGradient] = useState(!user?.photoURL);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const carouselRef = useRef<HTMLDivElement>(null);

  // Admin check
  const isAdmin = user?.email === 'jennifer.j.camilleri@gmail.com';

  // New preference states
  const [foodPreferences, setFoodPreferences] = useState<string[]>([]);
  const [householdMembers, setHouseholdMembers] = useState<{ name: string; age: number }[]>([]);
  const [takeawayNight, setTakeawayNight] = useState<string>('');
  const [allergies, setAllergies] = useState<string[]>([]);
  const [selectedHouseholdMember, setSelectedHouseholdMember] = useState<number | null>(null);
  const [householdMemberColors, setHouseholdMemberColors] = useState<{ [key: number]: string }>({});

  // Nest Test Modal state
  const [isNestTestModalOpen, setIsNestTestModalOpen] = useState(false);
  const [completedTests, setCompletedTests] = useState<string[]>([]);
  const [nestTestData, setNestTestData] = useState<{
    rooms?: { [key: string]: number };
    homeTimes?: { member: string; homeFrom: string; homeTo: string }[];
  }>({});

  // Taste Test Modal state
  const [isTasteTestModalOpen, setIsTasteTestModalOpen] = useState(false);
  const [tasteTestData, setTasteTestData] = useState<{
    foodPreferences?: {
      cuisines: string[];
      flavors: string[];
      mealTypes: string[];
      snackTypes: string[];
    };
    dietaryRestrictions?: Array<{
      name: string;
      type: 'allergy' | 'intolerance' | 'preference' | 'lifestyle';
    }>;
    cookingHabits?: {
      frequency: string;
      preferredMealTypes: string[];
      cookingSkillLevel: string;
      timeAvailable: string;
    };
    budgetRange?: string;
    shoppingPreferences?: string[];
  }>({});

  // Mess Test Modal state
  const [isMessTestModalOpen, setIsMessTestModalOpen] = useState(false);
  const [messTestData, setMessTestData] = useState<{
    choreAssignments: Array<{
      member: string;
      chores: string[];
    }>;
    noChoresDays: string[];
    productiveDays: string[];
    stepByStepPreference: boolean;
    choreFrequency: { [key: string]: string };
    chorePreferences: {
      preferredTime: string;
      motivationStyle: string;
      choreStyle: string;
    };
  }>({
    choreAssignments: [],
    noChoresDays: [],
    productiveDays: [],
    stepByStepPreference: false,
    choreFrequency: {},
    chorePreferences: {
      preferredTime: '',
      motivationStyle: '',
      choreStyle: ''
    }
  });

  // Original values for change detection
  const [originalDisplayName, setOriginalDisplayName] = useState('');
  const [originalProfileImage, setOriginalProfileImage] = useState<string | null>(null);
  const [originalSelectedGradient, setOriginalSelectedGradient] = useState<string>(gradientOptions[0]);
  const [originalIsUsingGradient, setOriginalIsUsingGradient] = useState(false);
  const [originalFoodPreferences, setOriginalFoodPreferences] = useState<string[]>([]);
  const [originalHouseholdMembers, setOriginalHouseholdMembers] = useState<{ name: string; age: number }[]>([]);
  const [originalTakeawayNight, setOriginalTakeawayNight] = useState<string>('');
  const [originalAllergies, setOriginalAllergies] = useState<string[]>([]);
  const [originalHouseholdMemberColors, setOriginalHouseholdMemberColors] = useState<{ [key: number]: string }>({});
  const [originalTasteTestData, setOriginalTasteTestData] = useState<typeof tasteTestData>({});
  const [originalNestTestData, setOriginalNestTestData] = useState<typeof nestTestData>({});
  const [originalMessTestData, setOriginalMessTestData] = useState<typeof messTestData>({
    choreAssignments: [],
    noChoresDays: [],
    productiveDays: [],
    stepByStepPreference: false,
    choreFrequency: {},
    chorePreferences: {
      preferredTime: '',
      motivationStyle: '',
      choreStyle: ''
    }
  });

  // Check if any changes have been made
  const hasChanges = () => {
    return (
      displayName !== originalDisplayName ||
      profileImage !== originalProfileImage ||
      selectedGradient !== originalSelectedGradient ||
      isUsingGradient !== originalIsUsingGradient ||
      JSON.stringify(foodPreferences) !== JSON.stringify(originalFoodPreferences) ||
      JSON.stringify(householdMembers) !== JSON.stringify(originalHouseholdMembers) ||
      takeawayNight !== originalTakeawayNight ||
      JSON.stringify(allergies) !== JSON.stringify(originalAllergies) ||
      JSON.stringify(householdMemberColors) !== JSON.stringify(originalHouseholdMemberColors) ||
      JSON.stringify(tasteTestData) !== JSON.stringify(originalTasteTestData) ||
      JSON.stringify(nestTestData) !== JSON.stringify(originalNestTestData) ||
      JSON.stringify(messTestData) !== JSON.stringify(originalMessTestData)
    );
  };

  // Load user profile data on component mount
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user) return;
      
      try {
        const userDocRef = doc(db, 'userProfiles', user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const profileData = userDoc.data() as UserProfileData;
          if (profileData.selectedGradient) {
            setSelectedGradient(profileData.selectedGradient);
            setOriginalSelectedGradient(profileData.selectedGradient);
          }
          if (profileData.isUsingGradient !== undefined) {
            setIsUsingGradient(profileData.isUsingGradient);
            setOriginalIsUsingGradient(profileData.isUsingGradient);
          }
          // Always set the data, even if it's empty arrays/objects
          setFoodPreferences(profileData.foodPreferences || []);
          setOriginalFoodPreferences([...(profileData.foodPreferences || [])]);
          
          setHouseholdMembers(profileData.householdMembers || []);
          setOriginalHouseholdMembers([...(profileData.householdMembers || [])]);
          
          setTakeawayNight(profileData.takeawayNight || '');
          setOriginalTakeawayNight(profileData.takeawayNight || '');
          
          setAllergies(profileData.allergies || []);
          setOriginalAllergies([...(profileData.allergies || [])]);
          
          setHouseholdMemberColors(profileData.householdMemberColors || {});
          setOriginalHouseholdMemberColors(profileData.householdMemberColors || {});
          
          setCompletedTests(profileData.completedTests || []);
          
          setNestTestData(profileData.nestTestData || {});
          setOriginalNestTestData(JSON.parse(JSON.stringify(profileData.nestTestData || {})));
          
          setTasteTestData(profileData.tasteTestData || {});
          setOriginalTasteTestData(JSON.parse(JSON.stringify(profileData.tasteTestData || {})));
          
          setMessTestData(profileData.messTestData || {
            choreAssignments: [],
            noChoresDays: [],
            productiveDays: [],
            stepByStepPreference: false,
            choreFrequency: {},
            chorePreferences: {
              preferredTime: '',
              motivationStyle: '',
              choreStyle: ''
            }
          });
          setOriginalMessTestData(JSON.parse(JSON.stringify(profileData.messTestData || {
            choreAssignments: [],
            noChoresDays: [],
            productiveDays: [],
            stepByStepPreference: false,
            choreFrequency: {},
            chorePreferences: {
              preferredTime: '',
              motivationStyle: '',
              choreStyle: ''
            }
          })));
        } else {
          // Set defaults if no profile exists
          setIsUsingGradient(!user.photoURL);
          setOriginalIsUsingGradient(!user.photoURL);
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserProfile();
  }, [user]);

  // Update state when user changes
  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName || '');
      setOriginalDisplayName(user.displayName || '');
      setProfileImage(user.photoURL || null);
      setOriginalProfileImage(user.photoURL || null);
      if (user.photoURL) {
        setIsUsingGradient(false);
        setOriginalIsUsingGradient(false);
      }
    }
  }, [user]);

  const handleGradientSelect = (gradient: string) => {
    setSelectedGradient(gradient);
    setIsUsingGradient(true);
    setProfileImage(null);
  };

  const saveUserProfileData = async (profileData: UserProfileData) => {
    if (!user) return;
    
    try {
      const userDocRef = doc(db, 'userProfiles', user.uid);
      await setDoc(userDocRef, profileData, { merge: true });
    } catch (error) {
      console.error('Error saving user profile data:', error);
      throw error;
    }
  };

  const handleSave = async () => {
    if (!user) return;
    
    setIsSaving(true);
    try {
      // Update Firebase Auth profile
      await updateProfile(user, {
        displayName: displayName.trim() || user.email?.split('@')[0] || 'User',
        photoURL: isUsingGradient ? null : profileImage,
      });

      // Save additional profile data to Firestore
      await saveUserProfileData({
        selectedGradient,
        isUsingGradient,
        foodPreferences,
        householdMembers,
        takeawayNight,
        allergies,
        householdMemberColors,
        completedTests,
        nestTestData,
        tasteTestData,
        messTestData,
      });

      // Force a refresh of the auth state to trigger navigation update
      await user.reload();
      
      // Trigger a custom event to notify AppLayout to refresh profile data
      window.dispatchEvent(new CustomEvent('profileUpdated'));
      
      // Update original values after successful save
      setOriginalDisplayName(displayName);
      setOriginalProfileImage(profileImage);
      setOriginalSelectedGradient(selectedGradient);
      setOriginalIsUsingGradient(isUsingGradient);
      setOriginalFoodPreferences([...foodPreferences]);
      setOriginalHouseholdMembers([...householdMembers]);
      setOriginalTakeawayNight(takeawayNight);
      setOriginalAllergies([...allergies]);
      setOriginalHouseholdMemberColors(householdMemberColors);
      setOriginalTasteTestData(JSON.parse(JSON.stringify(tasteTestData)));
      setOriginalNestTestData(JSON.parse(JSON.stringify(nestTestData)));
      setOriginalMessTestData(JSON.parse(JSON.stringify(messTestData)));
      
      setIsEditing(false);
      setIsEditingAvatar(false);
      setIsEditingName(false);
      setIsEditingPreferences(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditingSection(null);
  };

  const handleStopEditing = () => {
    // Reset all values to original
    setDisplayName(originalDisplayName);
    setProfileImage(originalProfileImage);
    setSelectedGradient(originalSelectedGradient);
    setIsUsingGradient(originalIsUsingGradient);
    setFoodPreferences([...originalFoodPreferences]);
    setHouseholdMembers([...originalHouseholdMembers]);
    setTakeawayNight(originalTakeawayNight);
    setAllergies([...originalAllergies]);
    setHouseholdMemberColors(originalHouseholdMemberColors);
    setTasteTestData(JSON.parse(JSON.stringify(originalTasteTestData)));
    setNestTestData(JSON.parse(JSON.stringify(originalNestTestData)));
    setMessTestData(JSON.parse(JSON.stringify(originalMessTestData)));
    
    setIsEditing(false);
    setIsEditingAvatar(false);
    setIsEditingName(false);
    setIsEditingPreferences(false);
    setEditingSection(null);
  };

  const handleEditAvatar = () => {
    setIsEditingAvatar(!isEditingAvatar);
    if (isEditingName) setIsEditingName(false); // Close name editing if open
  };

  const handleEditName = () => {
    setIsEditingName(!isEditingName);
    if (isEditingAvatar) setIsEditingAvatar(false); // Close avatar editing if open
  };

  const handleCancelAvatar = () => {
    setIsEditingAvatar(false);
    setProfileImage(user?.photoURL || null);
    // Reset to saved gradient preferences
    loadUserProfile();
  };

  const handleCancelName = () => {
    setIsEditingName(false);
    setDisplayName(user?.displayName || '');
  };

  const handleCancel = () => {
    setDisplayName(user?.displayName || '');
    setProfileImage(user?.photoURL || null);
    // Reset to saved gradient preferences
    loadUserProfile();
    setIsEditing(false);
    setIsEditingAvatar(false);
    setIsEditingName(false);
    setIsEditingPreferences(false);
    setEditingSection(null);
  };

  const loadUserProfile = async () => {
    if (!user) return;
    
    try {
      const userDocRef = doc(db, 'userProfiles', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const profileData = userDoc.data() as UserProfileData;
        if (profileData.selectedGradient) {
          setSelectedGradient(profileData.selectedGradient);
          setOriginalSelectedGradient(profileData.selectedGradient);
        }
        if (profileData.isUsingGradient !== undefined) {
          setIsUsingGradient(profileData.isUsingGradient);
          setOriginalIsUsingGradient(profileData.isUsingGradient);
        }
        
        // Always set the data, even if it's empty arrays/objects
        setFoodPreferences(profileData.foodPreferences || []);
        setOriginalFoodPreferences([...(profileData.foodPreferences || [])]);
        
        setHouseholdMembers(profileData.householdMembers || []);
        setOriginalHouseholdMembers([...(profileData.householdMembers || [])]);
        
        setTakeawayNight(profileData.takeawayNight || '');
        setOriginalTakeawayNight(profileData.takeawayNight || '');
        
        setAllergies(profileData.allergies || []);
        setOriginalAllergies([...(profileData.allergies || [])]);
        
        setHouseholdMemberColors(profileData.householdMemberColors || {});
        setOriginalHouseholdMemberColors(profileData.householdMemberColors || {});
        
        setCompletedTests(profileData.completedTests || []);
        
        setNestTestData(profileData.nestTestData || {});
        setOriginalNestTestData(JSON.parse(JSON.stringify(profileData.nestTestData || {})));
        
        setTasteTestData(profileData.tasteTestData || {});
        setOriginalTasteTestData(JSON.parse(JSON.stringify(profileData.tasteTestData || {})));
        
        setMessTestData(profileData.messTestData || {
          choreAssignments: [],
          noChoresDays: [],
          productiveDays: [],
          stepByStepPreference: false,
          choreFrequency: {},
          chorePreferences: {
            preferredTime: '',
            motivationStyle: '',
            choreStyle: ''
          }
        });
        setOriginalMessTestData(JSON.parse(JSON.stringify(profileData.messTestData || {
          choreAssignments: [],
          noChoresDays: [],
          productiveDays: [],
          stepByStepPreference: false,
          choreFrequency: {},
          chorePreferences: {
            preferredTime: '',
            motivationStyle: '',
            choreStyle: ''
          }
        })));
      } else {
        // Set defaults if no profile exists
        setIsUsingGradient(!user.photoURL);
        setOriginalIsUsingGradient(!user.photoURL);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const currentDisplayName = displayName || user?.email?.split('@')[0] || 'User';

  const handleEditPreferences = () => {
    setIsEditingPreferences(!isEditingPreferences);
    if (isEditingAvatar) setIsEditingAvatar(false);
    if (isEditingName) setIsEditingName(false);
    setEditingSection(null);
  };

  const handleEditSection = (sectionName: string) => {
    const newEditingSection = editingSection === sectionName ? null : sectionName;
    setEditingSection(newEditingSection);
    setIsEditingPreferences(false);
    setIsEditingAvatar(false);
    setIsEditingName(false);
    
    // Auto-select first household member when opening chore assignments editor
    if (newEditingSection === 'chore-assignments' && householdMembers.length > 0) {
      setSelectedHouseholdMember(0);
    } else if (newEditingSection !== 'chore-assignments') {
      setSelectedHouseholdMember(null);
    }
  };

  const addHouseholdMember = (name?: string, age?: number) => {
    setHouseholdMembers([...householdMembers, { name: name || '', age: age || 0 }]);
  };

  const removeHouseholdMember = (index: number) => {
    setHouseholdMembers(householdMembers.filter((_, i) => i !== index));
  };

  const updateHouseholdMember = (index: number, field: 'name' | 'age', value: string | number) => {
    const updated = [...householdMembers];
    updated[index] = { ...updated[index], [field]: value };
    setHouseholdMembers(updated);
  };

  const addFoodPreference = (preference: string) => {
    if (preference && !foodPreferences.includes(preference)) {
      setFoodPreferences([...foodPreferences, preference]);
    }
  };

  const removeFoodPreference = (preference: string) => {
    setFoodPreferences(foodPreferences.filter(p => p !== preference));
  };

  const addAllergy = (allergy: string) => {
    if (allergy && !allergies.includes(allergy)) {
      setAllergies([...allergies, allergy]);
    }
  };

  const removeAllergy = (allergy: string) => {
    setAllergies(allergies.filter(a => a !== allergy));
  };

  // Taste Test Data Helper Functions
  const addTasteCuisine = (cuisine: string) => {
    if (!cuisine.trim()) return;
    setTasteTestData(prev => ({
      ...prev,
      foodPreferences: {
        ...prev.foodPreferences,
        cuisines: [...(prev.foodPreferences?.cuisines || []), cuisine.trim()],
        flavors: prev.foodPreferences?.flavors || [],
        mealTypes: prev.foodPreferences?.mealTypes || [],
        snackTypes: prev.foodPreferences?.snackTypes || []
      }
    }));
  };

  const removeTasteCuisine = (cuisine: string) => {
    setTasteTestData(prev => ({
      ...prev,
      foodPreferences: {
        ...prev.foodPreferences,
        cuisines: (prev.foodPreferences?.cuisines || []).filter(c => c !== cuisine),
        flavors: prev.foodPreferences?.flavors || [],
        mealTypes: prev.foodPreferences?.mealTypes || [],
        snackTypes: prev.foodPreferences?.snackTypes || []
      }
    }));
  };

  const addTasteFlavor = (flavor: string) => {
    if (!flavor.trim()) return;
    setTasteTestData(prev => ({
      ...prev,
      foodPreferences: {
        ...prev.foodPreferences,
        cuisines: prev.foodPreferences?.cuisines || [],
        flavors: [...(prev.foodPreferences?.flavors || []), flavor.trim()],
        mealTypes: prev.foodPreferences?.mealTypes || [],
        snackTypes: prev.foodPreferences?.snackTypes || []
      }
    }));
  };

  const removeTasteFlavor = (flavor: string) => {
    setTasteTestData(prev => ({
      ...prev,
      foodPreferences: {
        ...prev.foodPreferences,
        cuisines: prev.foodPreferences?.cuisines || [],
        flavors: (prev.foodPreferences?.flavors || []).filter(f => f !== flavor),
        mealTypes: prev.foodPreferences?.mealTypes || [],
        snackTypes: prev.foodPreferences?.snackTypes || []
      }
    }));
  };

  const addDietaryRestriction = (restriction: string) => {
    if (!restriction.trim()) return;
    setTasteTestData(prev => ({
      ...prev,
      dietaryRestrictions: [
        ...(prev.dietaryRestrictions || []),
        { name: restriction.trim(), type: 'preference' as const }
      ]
    }));
  };

  const removeDietaryRestriction = (restrictionName: string) => {
    setTasteTestData(prev => ({
      ...prev,
      dietaryRestrictions: (prev.dietaryRestrictions || []).filter(r => r.name !== restrictionName)
    }));
  };

  const updateCookingFrequency = (frequency: string) => {
    setTasteTestData(prev => ({
      ...prev,
      cookingHabits: {
        ...prev.cookingHabits,
        frequency,
        preferredMealTypes: prev.cookingHabits?.preferredMealTypes || [],
        cookingSkillLevel: prev.cookingHabits?.cookingSkillLevel || '',
        timeAvailable: prev.cookingHabits?.timeAvailable || ''
      }
    }));
  };

  const updateCookingSkillLevel = (skillLevel: string) => {
    setTasteTestData(prev => ({
      ...prev,
      cookingHabits: {
        ...prev.cookingHabits,
        frequency: prev.cookingHabits?.frequency || '',
        preferredMealTypes: prev.cookingHabits?.preferredMealTypes || [],
        cookingSkillLevel: skillLevel,
        timeAvailable: prev.cookingHabits?.timeAvailable || ''
      }
    }));
  };

  const updateTimeAvailable = (timeAvailable: string) => {
    setTasteTestData(prev => ({
      ...prev,
      cookingHabits: {
        ...prev.cookingHabits,
        frequency: prev.cookingHabits?.frequency || '',
        preferredMealTypes: prev.cookingHabits?.preferredMealTypes || [],
        cookingSkillLevel: prev.cookingHabits?.cookingSkillLevel || '',
        timeAvailable
      }
    }));
  };

  const addPreferredMealType = (mealType: string) => {
    if (!mealType.trim()) return;
    setTasteTestData(prev => ({
      ...prev,
      cookingHabits: {
        ...prev.cookingHabits,
        frequency: prev.cookingHabits?.frequency || '',
        preferredMealTypes: [...(prev.cookingHabits?.preferredMealTypes || []), mealType.trim()],
        cookingSkillLevel: prev.cookingHabits?.cookingSkillLevel || '',
        timeAvailable: prev.cookingHabits?.timeAvailable || ''
      }
    }));
  };

  const removePreferredMealType = (mealType: string) => {
    setTasteTestData(prev => ({
      ...prev,
      cookingHabits: {
        ...prev.cookingHabits,
        frequency: prev.cookingHabits?.frequency || '',
        preferredMealTypes: (prev.cookingHabits?.preferredMealTypes || []).filter(m => m !== mealType),
        cookingSkillLevel: prev.cookingHabits?.cookingSkillLevel || '',
        timeAvailable: prev.cookingHabits?.timeAvailable || ''
      }
    }));
  };

  const updateBudgetRange = (budget: string) => {
    setTasteTestData(prev => ({
      ...prev,
      budgetRange: budget
    }));
  };

  const addShoppingPreference = (preference: string) => {
    if (!preference.trim()) return;
    setTasteTestData(prev => ({
      ...prev,
      shoppingPreferences: [...(prev.shoppingPreferences || []), preference.trim()]
    }));
  };

  const removeShoppingPreference = (preference: string) => {
    setTasteTestData(prev => ({
      ...prev,
      shoppingPreferences: (prev.shoppingPreferences || []).filter(p => p !== preference)
    }));
  };

  // Mess Test Data Helper Functions for Days
  const toggleProductiveDay = (day: string) => {
    setMessTestData(prev => ({
      ...prev,
      productiveDays: prev.productiveDays.includes(day)
        ? prev.productiveDays.filter(d => d !== day)
        : [...prev.productiveDays, day]
    }));
  };

  const toggleNoChoreDay = (day: string) => {
    setMessTestData(prev => ({
      ...prev,
      noChoresDays: prev.noChoresDays.includes(day)
        ? prev.noChoresDays.filter(d => d !== day)
        : [...prev.noChoresDays, day]
    }));
  };

  // Chore Assignment Helper Functions
  const COMMON_CHORES = [
    'Dishes', 'Laundry', 'Vacuuming', 'Mopping', 'Dusting', 'Bathroom Cleaning',
    'Kitchen Cleaning', 'Trash/Recycling', 'Bed Making', 'Grocery Shopping',
    'Cooking', 'Meal Prep', 'Garden/Yard Work', 'Pet Care', 'Car Maintenance'
  ];

  const toggleChoreForMember = React.useCallback((memberName: string, chore: string) => {
    console.log('toggleChoreForMember called:', { memberName, chore });
    setMessTestData(prev => {
      console.log('Previous messTestData:', prev);
      // Find existing assignment or create new one
      const existingAssignmentIndex = prev.choreAssignments.findIndex(assignment => assignment.member === memberName);
      console.log('Existing assignment index:', existingAssignmentIndex);
      
      if (existingAssignmentIndex >= 0) {
        // Update existing assignment
        const updatedAssignments = [...prev.choreAssignments];
        const assignment = updatedAssignments[existingAssignmentIndex];
        const isSelected = assignment.chores.includes(chore);
        console.log('Updating existing assignment. Was selected:', isSelected);
        
        updatedAssignments[existingAssignmentIndex] = {
          ...assignment,
          chores: isSelected 
            ? assignment.chores.filter(c => c !== chore)
            : [...assignment.chores, chore]
        };
        
        const newData = {
          ...prev,
          choreAssignments: updatedAssignments
        };
        console.log('New messTestData after update:', newData);
        return newData;
      } else {
        // Create new assignment for this member
        console.log('Creating new assignment for member');
        const newData = {
          ...prev,
          choreAssignments: [
            ...prev.choreAssignments,
            { member: memberName, chores: [chore] }
          ]
        };
        console.log('New messTestData after creation:', newData);
        return newData;
      }
    });
  }, []);

  // Home Rooms editing functions
  const addRoom = (roomName: string) => {
    if (!roomName.trim()) return;
    const rooms = nestTestData.rooms || {};
    const roomsArray = Object.keys(rooms);
    if (!roomsArray.includes(roomName.trim())) {
      setNestTestData(prev => ({
        ...prev,
        rooms: {
          ...prev.rooms,
          [roomName.trim()]: 1
        }
      }));
    }
  };

  const incrementRoomCount = (roomName: string) => {
    setNestTestData(prev => ({
      ...prev,
      rooms: {
        ...prev.rooms,
        [roomName]: (prev.rooms?.[roomName] || 0) + 1
      }
    }));
  };

  const decrementRoomCount = (roomName: string) => {
    setNestTestData(prev => {
      const currentCount = prev.rooms?.[roomName] || 0;
      if (currentCount <= 1) {
        // If count is 1 or less, remove the room entirely
        const newRooms = { ...prev.rooms };
        delete newRooms[roomName];
        return {
          ...prev,
          rooms: newRooms
        };
      } else {
        // Otherwise, just decrement the count
        return {
          ...prev,
          rooms: {
            ...prev.rooms,
            [roomName]: currentCount - 1
          }
        };
      }
    });
  };

  const removeRoom = (roomName: string) => {
    setNestTestData(prev => {
      const newRooms = { ...prev.rooms };
      delete newRooms[roomName];
      return {
        ...prev,
        rooms: newRooms
      };
    });
  };

  // Home Times editing functions
  const addHomeTime = async (member: string, homeFrom: string, homeTo: string) => {
    if (!member.trim()) return;
    
    const newNestTestData = {
      ...nestTestData,
      homeTimes: [
        ...(nestTestData.homeTimes || []),
        { member: member.trim(), homeFrom: homeFrom.trim(), homeTo: homeTo.trim() }
      ]
    };
    
    setNestTestData(newNestTestData);
    
    // Auto-save to Firestore
    if (user) {
      try {
        await saveUserProfileData({
          selectedGradient,
          isUsingGradient,
          foodPreferences,
          householdMembers,
          takeawayNight,
          allergies,
          householdMemberColors,
          completedTests,
          nestTestData: newNestTestData,
          tasteTestData,
          messTestData
        });
        
        // Update original values
        setOriginalNestTestData(JSON.parse(JSON.stringify(newNestTestData)));
        
        // Trigger profile update event
        window.dispatchEvent(new CustomEvent('profileUpdated'));
      } catch (error) {
        console.error('Error auto-saving home time:', error);
      }
    }
  };

  const removeHomeTime = async (index: number) => {
    const newNestTestData = {
      ...nestTestData,
      homeTimes: (nestTestData.homeTimes || []).filter((_, i) => i !== index)
    };
    
    setNestTestData(newNestTestData);
    
    // Auto-save to Firestore
    if (user) {
      try {
        await saveUserProfileData({
          selectedGradient,
          isUsingGradient,
          foodPreferences,
          householdMembers,
          takeawayNight,
          allergies,
          householdMemberColors,
          completedTests,
          nestTestData: newNestTestData,
          tasteTestData,
          messTestData
        });
        
        // Update original values
        setOriginalNestTestData(JSON.parse(JSON.stringify(newNestTestData)));
        
        // Trigger profile update event
        window.dispatchEvent(new CustomEvent('profileUpdated'));
      } catch (error) {
        console.error('Error auto-saving home time removal:', error);
      }
    }
  };

  const updateHomeTime = async (index: number, field: 'member' | 'homeFrom' | 'homeTo', value: string) => {
    const newNestTestData = {
      ...nestTestData,
      homeTimes: [...(nestTestData.homeTimes || [])]
    };
    newNestTestData.homeTimes[index] = { ...newNestTestData.homeTimes[index], [field]: value };
    
    setNestTestData(newNestTestData);
    
    // Auto-save to Firestore
    if (user) {
      try {
        await saveUserProfileData({
          selectedGradient,
          isUsingGradient,
          foodPreferences,
          householdMembers,
          takeawayNight,
          allergies,
          householdMemberColors,
          completedTests,
          nestTestData: newNestTestData,
          tasteTestData,
          messTestData
        });
        
        // Update original values
        setOriginalNestTestData(JSON.parse(JSON.stringify(newNestTestData)));
        
        // Trigger profile update event
        window.dispatchEvent(new CustomEvent('profileUpdated'));
      } catch (error) {
        console.error('Error auto-saving home time:', error);
      }
    }
  };

  const handleHouseholdMemberSelect = (index: number) => {
    setSelectedHouseholdMember(selectedHouseholdMember === index ? null : index);
  };

  const handleColorSelect = async (index: number, color: string) => {
    const newColors = {
      ...householdMemberColors,
      [index]: color
    };
    
    setHouseholdMemberColors(newColors);
    setSelectedHouseholdMember(null);
    
    // Auto-save to Firestore immediately
    if (user) {
      try {
        await saveUserProfileData({
          selectedGradient,
          isUsingGradient,
          foodPreferences,
          householdMembers,
          takeawayNight,
          allergies,
          householdMemberColors: newColors,
        });
        
        // Update the original values so it doesn't show as unsaved changes
        setOriginalHouseholdMemberColors(newColors);
        
        // Trigger a custom event to notify AppLayout to refresh profile data
        window.dispatchEvent(new CustomEvent('profileUpdated'));
      } catch (error) {
        console.error('Error auto-saving household member color:', error);
      }
    }
  };

  // Handle click outside to close color picker (but not when editing chore assignments)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (selectedHouseholdMember !== null && 
          editingSection !== 'chore-assignments' && // Don't close when editing chore assignments
          !target.closest('[data-color-picker]') && 
          !target.closest('[data-household-tag]') &&
          !target.closest('[data-chore-tag]')) { // Don't close when clicking chore tags
        setSelectedHouseholdMember(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedHouseholdMember, editingSection]);

  // Nest Test Modal handlers
  const handleNestTestClick = () => {
    if (!completedTests.includes('nestTest')) {
      setIsNestTestModalOpen(true);
    }
  };

  const handleNestTestSubmit = async (data: {
    householdMembers: { name: string; age: number }[];
    rooms: { [key: string]: number };
    homeTimes: { member: string; homeFrom: string; homeTo: string }[];
    memberColors: { [key: number]: string };
  }) => {
    try {
      // Update household members
      setHouseholdMembers(data.householdMembers);
      
      // Update household member colors from the modal
      setHouseholdMemberColors(data.memberColors);
      
      // Update nest test data
      setNestTestData({
        rooms: data.rooms,
        homeTimes: data.homeTimes
      });
      
      // Mark test as completed
      const updatedCompletedTests = [...completedTests, 'nestTest'];
      setCompletedTests(updatedCompletedTests);
      
      // Save to Firestore
      if (user) {
        await saveUserProfileData({
          selectedGradient,
          isUsingGradient,
          foodPreferences,
          householdMembers: data.householdMembers,
          takeawayNight,
          allergies,
          householdMemberColors: data.memberColors,
          completedTests: updatedCompletedTests,
          nestTestData: {
            rooms: data.rooms,
            homeTimes: data.homeTimes
          }
        });
        
        // Update original values
        setOriginalHouseholdMembers(data.householdMembers);
        setOriginalHouseholdMemberColors(data.memberColors);
        
        // Trigger profile update event
        window.dispatchEvent(new CustomEvent('profileUpdated'));
      }
    } catch (error) {
      console.error('Error saving nest test data:', error);
    }
  };

  // Taste Test Modal handlers
  const handleTasteTestClick = () => {
    if (!completedTests.includes('tasteTest')) {
      setIsTasteTestModalOpen(true);
    }
  };

  // Mess Test Modal handlers
  const handleMessTestClick = () => {
    if (!completedTests.includes('messTest')) {
      // Check if nest test is completed first
      if (!completedTests.includes('nestTest')) {
        alert('Please complete The Nest Test first before taking The Mess Test. We need to know who\'s in your household to assign chores!');
        return;
      }
      setIsMessTestModalOpen(true);
    }
  };

  const handleTasteTestSubmit = async (data: {
    foodPreferences: {
      cuisines: string[];
      flavors: string[];
      mealTypes: string[];
      snackTypes: string[];
    };
    dietaryRestrictions: Array<{
      name: string;
      type: 'allergy' | 'intolerance' | 'preference' | 'lifestyle';
    }>;
    cookingHabits: {
      frequency: string;
      preferredMealTypes: string[];
      cookingSkillLevel: string;
      timeAvailable: string;
    };
    takeawayNight: string;
    budgetRange: string;
    shoppingPreferences: string[];
  }) => {
    try {
      // Update taste test data
      setTasteTestData({
        foodPreferences: data.foodPreferences,
        dietaryRestrictions: data.dietaryRestrictions,
        cookingHabits: data.cookingHabits,
        budgetRange: data.budgetRange,
        shoppingPreferences: data.shoppingPreferences
      });
      
      // Update takeaway night in the main state
      if (data.takeawayNight) {
        setTakeawayNight(data.takeawayNight);
      }
      
      // Update food preferences and allergies from the detailed data
      const newFoodPrefs = [
        ...data.foodPreferences.cuisines,
        ...data.foodPreferences.flavors,
        ...data.cookingHabits.preferredMealTypes
      ].filter(Boolean);
      setFoodPreferences(newFoodPrefs);
      
      const newAllergies = data.dietaryRestrictions
        .filter(restriction => restriction.type === 'allergy' || restriction.type === 'intolerance')
        .map(restriction => restriction.name);
      setAllergies(newAllergies);
      
      // Mark test as completed
      const updatedCompletedTests = [...completedTests, 'tasteTest'];
      setCompletedTests(updatedCompletedTests);
      
      // Save to Firestore
      if (user) {
        await saveUserProfileData({
          selectedGradient,
          isUsingGradient,
          foodPreferences: newFoodPrefs,
          householdMembers,
          takeawayNight: data.takeawayNight || takeawayNight,
          allergies: newAllergies,
          householdMemberColors,
          completedTests: updatedCompletedTests,
          nestTestData,
          tasteTestData: {
            foodPreferences: data.foodPreferences,
            dietaryRestrictions: data.dietaryRestrictions,
            cookingHabits: data.cookingHabits,
            budgetRange: data.budgetRange,
            shoppingPreferences: data.shoppingPreferences
          }
        });
        
        // Update original values
        setOriginalFoodPreferences([...newFoodPrefs]);
        setOriginalAllergies([...newAllergies]);
        if (data.takeawayNight) {
          setOriginalTakeawayNight(data.takeawayNight);
        }
        
        // Trigger profile update event
        window.dispatchEvent(new CustomEvent('profileUpdated'));
      }
    } catch (error) {
      console.error('Error saving taste test data:', error);
    }
  };

  const handleMessTestSubmit = async (data: {
    choreAssignments: Array<{
      member: string;
      chores: string[];
    }>;
    noChoresDays: string[];
    productiveDays: string[];
    stepByStepPreference: boolean;
    choreFrequency: { [key: string]: string };
    chorePreferences: {
      preferredTime: string;
      motivationStyle: string;
      choreStyle: string;
    };
  }) => {
    try {
      // Update mess test data
      setMessTestData(data);
      
      // Mark test as completed
      const updatedCompletedTests = [...completedTests, 'messTest'];
      setCompletedTests(updatedCompletedTests);
      
      // Save to Firestore
      if (user) {
        await saveUserProfileData({
          selectedGradient,
          isUsingGradient,
          foodPreferences,
          householdMembers,
          takeawayNight,
          allergies,
          householdMemberColors,
          completedTests: updatedCompletedTests,
          nestTestData,
          tasteTestData,
          messTestData: data
        });
        
        // Update original values
        setOriginalMessTestData(JSON.parse(JSON.stringify(data)));
        
        // Trigger profile update event
        window.dispatchEvent(new CustomEvent('profileUpdated'));
      }
    } catch (error) {
      console.error('Error saving mess test data:', error);
    }
  };

  // Admin function to reset profile
  const handleResetProfile = async () => {
    if (!user || !isAdmin) return;
    
    const confirmReset = window.confirm(
      'Are you sure you want to reset this profile? This will:\n\n' +
      '• Clear all food preferences and allergies\n' +
      '• Remove all household members\n' +
      '• Reset takeaway night\n' +
      '• Clear all test completions\n' +
      '• Remove all test data\n\n' +
      'This action cannot be undone!'
    );
    
    if (!confirmReset) return;
    
    try {
      setIsSaving(true);
      
      // Reset all state to defaults
      setFoodPreferences([]);
      setHouseholdMembers([]);
      setTakeawayNight('');
      setAllergies([]);
      setHouseholdMemberColors({});
      setCompletedTests([]);
      setNestTestData({});
      setTasteTestData({});
      setMessTestData({
        choreAssignments: [],
        noChoresDays: [],
        productiveDays: [],
        stepByStepPreference: false,
        choreFrequency: {},
        chorePreferences: {
          preferredTime: '',
          motivationStyle: '',
          choreStyle: ''
        }
      });
      setSelectedHouseholdMember(null);
      
      // Reset to default gradient
      setSelectedGradient(gradientOptions[0]);
      setIsUsingGradient(!user.photoURL);
      
      // Save the reset profile to Firestore (completely replace the document)
      const userDocRef = doc(db, 'userProfiles', user.uid);
      await setDoc(userDocRef, {
        selectedGradient: gradientOptions[0],
        isUsingGradient: !user.photoURL,
        foodPreferences: [],
        householdMembers: [],
        takeawayNight: '',
        allergies: [],
        householdMemberColors: {},
        completedTests: [],
        nestTestData: {},
        tasteTestData: {},
        messTestData: {
          choreAssignments: [],
          noChoresDays: [],
          productiveDays: [],
          stepByStepPreference: false,
          choreFrequency: {},
          chorePreferences: {
            preferredTime: '',
            motivationStyle: '',
            choreStyle: ''
          }
        }
      }, { merge: false }); // Don't merge, completely replace the document
      
      // Update original values
      setOriginalFoodPreferences([]);
      setOriginalHouseholdMembers([]);
      setOriginalTakeawayNight('');
      setOriginalAllergies([]);
      setOriginalHouseholdMemberColors({});
      setOriginalSelectedGradient(gradientOptions[0]);
      setOriginalIsUsingGradient(!user.photoURL);
      setOriginalNestTestData({});
      setOriginalTasteTestData({});
      setOriginalMessTestData({
        choreAssignments: [],
        noChoresDays: [],
        productiveDays: [],
        stepByStepPreference: false,
        choreFrequency: {},
        chorePreferences: {
          preferredTime: '',
          motivationStyle: '',
          choreStyle: ''
        }
      });
      
      // Trigger profile update event
      window.dispatchEvent(new CustomEvent('profileUpdated'));
      
      alert('Profile has been successfully reset!');
    } catch (error) {
      console.error('Error resetting profile:', error);
      alert('Error resetting profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const scrollCarousel = (direction: 'left' | 'right') => {
    if (carouselRef.current) {
      const scrollAmount = 300; // Width of one card plus gap
      const currentScroll = carouselRef.current.scrollLeft;
      const targetScroll = direction === 'left' 
        ? currentScroll - scrollAmount 
        : currentScroll + scrollAmount;
      
      carouselRef.current.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      } as any);
    }
  };

  if (isLoading) {
    return (
      <ProfileContainer>
        <ProfileCard>
          <div style={{ display: 'flex', justifyContent: 'center', padding: theme.spacing.xl }}>
            Loading profile...
          </div>
        </ProfileCard>
      </ProfileContainer>
    );
  }

  return (
    <ProfileContainer>
      <ProfileCard>
        <ProfileSidePanel>
          {!isEditing && (
            <EditButton isActive={false} onClick={handleEdit}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
              </svg>
            </EditButton>
          )}

          {isEditing && !hasChanges() && (
            <EditButton isActive={true} onClick={handleStopEditing}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
              </svg>
            </EditButton>
          )}

          {isEditing && hasChanges() && (
            <SaveButton onClick={handleSave} disabled={isSaving}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z"/>
              </svg>
            </SaveButton>
          )}

          {isAdmin && (
            <ResetButton onClick={handleResetProfile} disabled={isSaving}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
              </svg>
            </ResetButton>
          )}

          <AvatarSection>
            <AvatarContainer>
              <Avatar
                backgroundImage={!isUsingGradient ? profileImage || undefined : undefined}
                gradient={isUsingGradient ? selectedGradient : undefined}
              >
                {(!profileImage || isUsingGradient) && getInitials(currentDisplayName)}
              </Avatar>
              <EditIconButton isEditing={isEditing && !isEditingName && !isEditingPreferences} onClick={handleEditAvatar}>
                <EditIconImage src="/images/EditICON.png" alt="Edit" />
              </EditIconButton>
            </AvatarContainer>

            {!isEditingName && (
              <NameDisplay>
                <NameText>{currentDisplayName}</NameText>
                <NameEditButton isEditing={isEditing && !isEditingAvatar && !isEditingPreferences} onClick={handleEditName}>
                  <NameEditIconImage src="/images/EditICON.png" alt="Edit" />
                </NameEditButton>
              </NameDisplay>
            )}

            {isEditingName && (
              <NameEditContainer>
                <NameEditInput
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Enter your name"
                  size={Math.max(displayName.length || 12, 12)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleEditName();
                    } else if (e.key === 'Escape') {
                      setDisplayName(user?.displayName || '');
                      handleEditName();
                    }
                  }}
                  autoFocus
                />
                <NameEditingButton onClick={handleEditName}>
                  <NameEditIconImage src="/images/EditICON.png" alt="Edit" />
                </NameEditingButton>
              </NameEditContainer>
            )}

            <GradientOptions isEditing={isEditingAvatar}>
              {gradientOptions.map((gradient, index) => (
                <GradientOption
                  key={index}
                  gradient={gradient}
                  isSelected={isUsingGradient && selectedGradient === gradient}
                  onClick={() => handleGradientSelect(gradient)}
                />
              ))}
            </GradientOptions>
          </AvatarSection>

          <TestButtonsSection>
            <CarouselContainer>
              <CarouselButton className="carousel-nav" onClick={() => scrollCarousel('left')}>
                ←
              </CarouselButton>
              
              <TestButtonsContainer ref={carouselRef}>
                {!completedTests.includes('nestTest') && (
                  <TestTile onClick={handleNestTestClick} bgColor={theme.colors.accent} textColor={theme.colors.accent}>
                    <TileContent textColor={theme.colors.accent}>
                      <div className="icon-container">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
                        </svg>
                      </div>
                      <div className="text-content">
                        <h3>The Nest Test</h3>
                        <p>Who's in your nest, how old they are, and who's doing (or dodging) the chores.</p>
                      </div>
                    </TileContent>
                  </TestTile>
                )}
                
                {!completedTests.includes('tasteTest') && (
                  <TestTile onClick={handleTasteTestClick} bgColor={theme.colors.secondary} textColor={theme.colors.secondary}>
                  <TileContent textColor={theme.colors.secondary}>
                    <div className="icon-container">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8.1 13.34l2.83-2.83L3.91 3.5c-1.56 1.56-1.56 4.09 0 5.66l4.19 4.18zm6.78-1.81c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.20-1.10-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z"/>
                      </svg>
                    </div>
                    <div className="text-content">
                      <h3>The Taste Test</h3>
                      <p>Spill the beans on your favourite foods, flavours you love (or loathe), and what's always on your plate.</p>
                    </div>
                  </TileContent>
                </TestTile>
                )}
                
                {!completedTests.includes('messTest') && (
                  <TestTile onClick={handleMessTestClick} bgColor={theme.colors.primary} textColor={theme.colors.primary}>
                    <TileContent textColor={theme.colors.primary}>
                      <div className="icon-container">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19.36 2.72L20.78 4.14L15.06 9.85C16.13 11.39 16.28 13.24 15.38 14.44L9.06 8.12C10.26 7.22 12.11 7.37 13.65 8.44L19.36 2.72M5.93 17.57C3.92 15.56 2.69 13.16 2.35 10.92L7.23 8.83L14.67 16.27L12.58 21.15C10.34 20.81 7.94 19.58 5.93 17.57Z"/>
                        </svg>
                      </div>
                      <div className="text-content">
                        <h3>The Mess Test</h3>
                        <p>Let us know how you feel about chores what you'll tackle, what you'll dodge, and everything in between.</p>
                      </div>
                    </TileContent>
                  </TestTile>
                )}
              </TestButtonsContainer>
              
              <CarouselButton className="carousel-nav" onClick={() => scrollCarousel('right')}>
                →
              </CarouselButton>
            </CarouselContainer>
          </TestButtonsSection>
        </ProfileSidePanel>

        <CenterSection>
          <PreferencesSection>
            <PreferencesHeader>
              <PreferencesTitle>Preferences</PreferencesTitle>
            </PreferencesHeader>

            {/* HOUSEHOLD & PERSONAL SECTION */}
            <PreferenceSubSection>
              <SectionHeader>
                <SubHeader>Household Members</SubHeader>
                <PreferenceEditButton 
                  isVisible={isEditing}
                  isEditing={editingSection === 'household-members'}
                  onClick={() => handleEditSection('household-members')}
                >
                  {editingSection === 'household-members' ? (
                    <span style={{ fontSize: '18px', color: '#666', lineHeight: 1 }}>×</span>
                  ) : (
                    <PreferenceEditIconImage src="/images/EditICON.png" alt="Edit" />
                  )}
                </PreferenceEditButton>
              </SectionHeader>
              {editingSection === 'household-members' && (
                <Input
                  placeholder="Add household member (Name, Age) and press Enter"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      const value = (e.target as HTMLInputElement).value.trim();
                      if (value) {
                        // Parse "Name, Age" format
                        const parts = value.split(',').map(part => part.trim());
                        if (parts.length >= 2) {
                          const name = parts[0];
                          const age = parseInt(parts[1]) || 0;
                          if (name && age > 0) {
                            addHouseholdMember(name, age);
                            (e.target as HTMLInputElement).value = '';
                          }
                        }
                      }
                    }
                  }}
                />
              )}
              <TagContainer>
                {householdMembers.length > 0 ? (
                  householdMembers.map((member, index) => (
                    <HouseholdTag 
                      key={index} 
                      customColor={householdMemberColors[index]}
                      isSelected={selectedHouseholdMember === index}
                      hasRemoveButton={editingSection === 'household-members'}
                      onClick={() => handleHouseholdMemberSelect(index)}
                      data-household-tag
                    >
                      {member.name} ({member.age})
                      {editingSection === 'household-members' && (
                        <button onClick={(e) => {
                          e.stopPropagation();
                          removeHouseholdMember(index);
                        }}>×</button>
                      )}
                    </HouseholdTag>
                  ))
                ) : (
                  <EmptyState>No household members specified</EmptyState>
                )}
              </TagContainer>
              
              <ColorPicker isVisible={selectedHouseholdMember !== null} data-color-picker>
                {householdColorOptions.map((color, colorIndex) => (
                  <ColorOption
                    key={colorIndex}
                    color={color}
                    isSelected={selectedHouseholdMember !== null && householdMemberColors[selectedHouseholdMember] === color}
                    onClick={() => selectedHouseholdMember !== null && handleColorSelect(selectedHouseholdMember, color)}
                  />
                ))}
              </ColorPicker>
            </PreferenceSubSection>

            {/* HOME & LIVING SECTION */}
            {nestTestData.rooms && Object.keys(nestTestData.rooms).length > 0 && (
              <PreferenceSubSection>
                <SectionHeader>
                  <SubHeader>Home Rooms</SubHeader>
                  <PreferenceEditButton 
                    isVisible={isEditing}
                    isEditing={editingSection === 'home-rooms'}
                    onClick={() => handleEditSection('home-rooms')}
                  >
                    {editingSection === 'home-rooms' ? (
                      <span style={{ fontSize: '18px', color: '#666', lineHeight: 1 }}>×</span>
                    ) : (
                      <PreferenceEditIconImage src="/images/EditICON.png" alt="Edit" />
                    )}
                  </PreferenceEditButton>
                </SectionHeader>
                {editingSection === 'home-rooms' && (
                  <Input
                    placeholder="Add room name and press Enter"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        addRoom((e.target as HTMLInputElement).value);
                        (e.target as HTMLInputElement).value = '';
                      }
                    }}
                  />
                )}
                <TagContainer>
                  {Object.keys(nestTestData.rooms).length > 0 ? (
                    Object.entries(nestTestData.rooms).map(([room, count], index) => (
                      <Tag 
                        key={index} 
                        selectedGradient={selectedGradient}
                        onClick={() => incrementRoomCount(room)}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          decrementRoomCount(room);
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        {room} {count > 1 && `(${count})`}
                        {editingSection === 'home-rooms' && (
                          <button onClick={(e) => {
                            e.stopPropagation();
                            removeRoom(room);
                          }}>×</button>
                        )}
                      </Tag>
                    ))
                  ) : (
                    <EmptyState>No rooms specified</EmptyState>
                  )}
                </TagContainer>
              </PreferenceSubSection>
            )}

            {nestTestData.homeTimes && nestTestData.homeTimes.length > 0 && (
              <PreferenceSubSection>
                <SectionHeader>
                  <SubHeader>Home Times</SubHeader>
                  <PreferenceEditButton 
                    isVisible={isEditing}
                    isEditing={editingSection === 'home-times'}
                    onClick={() => handleEditSection('home-times')}
                  >
                    {editingSection === 'home-times' ? (
                      <span style={{ fontSize: '18px', color: '#666', lineHeight: 1 }}>×</span>
                    ) : (
                      <PreferenceEditIconImage src="/images/EditICON.png" alt="Edit" />
                    )}
                  </PreferenceEditButton>
                </SectionHeader>
                <div>
                  {editingSection === 'home-times' ? (
                    // In edit mode, show all household members
                    householdMembers.map((member, memberIndex) => {
                                             // Find existing home time data for this member
                       const existingTimeIndex = nestTestData.homeTimes?.findIndex(timeData => timeData.member === member.name) ?? -1;
                       const timeData = existingTimeIndex >= 0 && nestTestData.homeTimes ? nestTestData.homeTimes[existingTimeIndex] : { member: member.name, homeFrom: '', homeTo: '' };
                      const memberColor = householdMemberColors[memberIndex];
                      
                      return (
                        <DetailRow key={memberIndex}>
                          <HouseholdTag customColor={memberColor}>
                            {member.name}
                          </HouseholdTag>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '12px', 
                            fontSize: '0.9rem',
                            minWidth: '380px'
                          }}>
                            <input
                              type="time"
                              value={timeData.homeFrom || ''}
                              onChange={async (e) => {
                                if (existingTimeIndex >= 0) {
                                  await updateHomeTime(existingTimeIndex, 'homeFrom', e.target.value);
                                } else {
                                  await addHomeTime(member.name, e.target.value, '');
                                }
                              }}
                              style={{ 
                                padding: '6px 8px', 
                                border: '1px solid #ddd', 
                                borderRadius: '6px',
                                fontSize: '0.9rem',
                                width: '140px'
                              }}
                            />
                            <span style={{ 
                              color: '#666', 
                              fontWeight: '500',
                              minWidth: '20px',
                              textAlign: 'center'
                            }}>to</span>
                            <input
                              type="time"
                              value={timeData.homeTo || ''}
                              onChange={async (e) => {
                                if (existingTimeIndex >= 0) {
                                  await updateHomeTime(existingTimeIndex, 'homeTo', e.target.value);
                                } else {
                                  await addHomeTime(member.name, '', e.target.value);
                                }
                              }}
                              style={{ 
                                padding: '6px 8px', 
                                border: '1px solid #ddd', 
                                borderRadius: '6px',
                                fontSize: '0.9rem',
                                width: '140px'
                              }}
                            />
                            {existingTimeIndex >= 0 && (timeData.homeFrom || timeData.homeTo) && (
                              <button 
                                onClick={() => removeHomeTime(existingTimeIndex)} 
                                style={{ 
                                  marginLeft: '4px',
                                  color: '#dc3545', 
                                  background: 'none', 
                                  border: 'none', 
                                  cursor: 'pointer',
                                  fontSize: '16px',
                                  fontWeight: 'bold',
                                  padding: '4px 6px',
                                  borderRadius: '4px',
                                  transition: 'background-color 0.2s ease'
                                }}
                                title="Clear schedule"
                                onMouseEnter={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#f8d7da'}
                                onMouseLeave={(e) => (e.target as HTMLButtonElement).style.backgroundColor = 'transparent'}
                              >
                                ×
                              </button>
                            )}
                          </div>
                        </DetailRow>
                      );
                    })
                  ) : (
                    // In view mode, only show members with actual schedules
                    nestTestData.homeTimes
                      ?.filter(timeData => timeData.member && (timeData.homeFrom || timeData.homeTo))
                      .map((timeData, index) => {
                        // Find the member's color
                        const memberIndex = householdMembers.findIndex(member => member.name === timeData.member);
                        const memberColor = memberIndex >= 0 ? householdMemberColors[memberIndex] : undefined;
                        
                        return (
                          <DetailRow key={index}>
                            <HouseholdTag customColor={memberColor}>
                              {timeData.member}
                            </HouseholdTag>
                            <span style={{ color: theme.colors.lightText, fontSize: '0.9rem' }}>
                              {timeData.homeFrom && timeData.homeTo 
                                ? `${timeData.homeFrom} - ${timeData.homeTo}`
                                : timeData.homeFrom 
                                  ? `From ${timeData.homeFrom}`
                                  : timeData.homeTo 
                                    ? `Until ${timeData.homeTo}`
                                    : 'No specific times'
                              }
                            </span>
                          </DetailRow>
                        );
                      }) || []
                  )}
                </div>
              </PreferenceSubSection>
            )}

            {/* FOOD & DIETARY SECTION */}
            <PreferenceSubSection>
              <SectionHeader>
                <SubHeader>Food Preferences</SubHeader>
                <PreferenceEditButton 
                  isVisible={isEditing}
                  isEditing={editingSection === 'food-preferences'}
                  onClick={() => handleEditSection('food-preferences')}
                >
                  {editingSection === 'food-preferences' ? (
                    <span style={{ fontSize: '18px', color: '#666', lineHeight: 1 }}>×</span>
                  ) : (
                    <PreferenceEditIconImage src="/images/EditICON.png" alt="Edit" />
                  )}
                </PreferenceEditButton>
              </SectionHeader>
              {editingSection === 'food-preferences' && (
                <Input
                  placeholder="Add food preference and press Enter"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      addFoodPreference((e.target as HTMLInputElement).value);
                      (e.target as HTMLInputElement).value = '';
                    }
                  }}
                />
              )}
              <TagContainer>
                {/* Basic food preferences */}
                {foodPreferences.map((pref, index) => (
                  <Tag key={`pref-${index}`} selectedGradient={selectedGradient}>
                    {pref}
                    {editingSection === 'food-preferences' && (
                      <button onClick={() => removeFoodPreference(pref)}>×</button>
                    )}
                  </Tag>
                ))}
                
                {/* Cuisines from taste test */}
                {tasteTestData.foodPreferences?.cuisines?.map((cuisine, index) => (
                  <Tag key={`cuisine-${index}`} selectedGradient={selectedGradient}>
                    {cuisine}
                    {editingSection === 'food-preferences' && (
                      <button onClick={() => removeTasteCuisine(cuisine)}>×</button>
                    )}
                  </Tag>
                ))}
                
                {/* Flavors from taste test */}
                {tasteTestData.foodPreferences?.flavors?.map((flavor, index) => (
                  <Tag key={`flavor-${index}`} selectedGradient={selectedGradient}>
                    {flavor}
                    {editingSection === 'food-preferences' && (
                      <button onClick={() => removeTasteFlavor(flavor)}>×</button>
                    )}
                  </Tag>
                ))}
                
                {/* Show empty state only if no preferences at all */}
                {foodPreferences.length === 0 && 
                 !tasteTestData.foodPreferences?.cuisines?.length && 
                 !tasteTestData.foodPreferences?.flavors?.length && 
                 editingSection !== 'food-preferences' && (
                  <EmptyState>No food preferences specified</EmptyState>
                )}
              </TagContainer>
            </PreferenceSubSection>

            <PreferenceSubSection>
              <SectionHeader>
                <SubHeader>Allergies</SubHeader>
                <PreferenceEditButton 
                  isVisible={isEditing}
                  isEditing={editingSection === 'allergies'}
                  onClick={() => handleEditSection('allergies')}
                >
                  {editingSection === 'allergies' ? (
                    <span style={{ fontSize: '18px', color: '#666', lineHeight: 1 }}>×</span>
                  ) : (
                    <PreferenceEditIconImage src="/images/EditICON.png" alt="Edit" />
                  )}
                </PreferenceEditButton>
              </SectionHeader>
              {editingSection === 'allergies' && (
                <Input
                  placeholder="Add allergy and press Enter"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      addAllergy((e.target as HTMLInputElement).value);
                      (e.target as HTMLInputElement).value = '';
                    }
                  }}
                />
              )}
              <TagContainer>
                {allergies.length > 0 ? (
                  allergies.map((allergy, index) => (
                    <Tag key={index} selectedGradient={selectedGradient}>
                      {allergy}
                      {editingSection === 'allergies' && (
                        <button onClick={() => removeAllergy(allergy)}>×</button>
                      )}
                    </Tag>
                  ))
                ) : (
                  <EmptyState>No allergies specified</EmptyState>
                )}
              </TagContainer>
            </PreferenceSubSection>

            {(tasteTestData.dietaryRestrictions && tasteTestData.dietaryRestrictions.length > 0) || editingSection === 'dietary-restrictions' ? (
              <PreferenceSubSection>
                <SectionHeader>
                  <SubHeader>Dietary Restrictions</SubHeader>
                  <PreferenceEditButton 
                    isVisible={isEditing}
                    isEditing={editingSection === 'dietary-restrictions'}
                    onClick={() => handleEditSection('dietary-restrictions')}
                  >
                    {editingSection === 'dietary-restrictions' ? (
                      <span style={{ fontSize: '18px', color: '#666', lineHeight: 1 }}>×</span>
                    ) : (
                      <PreferenceEditIconImage src="/images/EditICON.png" alt="Edit" />
                    )}
                  </PreferenceEditButton>
                </SectionHeader>
                {editingSection === 'dietary-restrictions' && (
                  <Input
                    placeholder="Add dietary restriction and press Enter"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        addDietaryRestriction((e.target as HTMLInputElement).value);
                        (e.target as HTMLInputElement).value = '';
                      }
                    }}
                  />
                )}
                <TagContainer>
                  {tasteTestData.dietaryRestrictions && tasteTestData.dietaryRestrictions.length > 0 ? (
                    tasteTestData.dietaryRestrictions.map((restriction, index) => (
                      <Tag key={index} selectedGradient={selectedGradient}>
                        {restriction.name}
                        {editingSection === 'dietary-restrictions' && (
                          <button onClick={() => removeDietaryRestriction(restriction.name)}>×</button>
                        )}
                      </Tag>
                    ))
                  ) : editingSection !== 'dietary-restrictions' ? (
                    <EmptyState>No dietary restrictions specified</EmptyState>
                  ) : null}
                </TagContainer>
              </PreferenceSubSection>
            ) : null}

            <PreferenceSubSection>
              <SectionHeader>
                <SubHeader>Takeaway Night</SubHeader>
                <PreferenceEditButton 
                  isVisible={isEditing}
                  isEditing={editingSection === 'takeaway-night'}
                  onClick={() => handleEditSection('takeaway-night')}
                >
                  {editingSection === 'takeaway-night' ? (
                    <span style={{ fontSize: '18px', color: '#666', lineHeight: 1 }}>×</span>
                  ) : (
                    <PreferenceEditIconImage src="/images/EditICON.png" alt="Edit" />
                  )}
                </PreferenceEditButton>
              </SectionHeader>
              {editingSection === 'takeaway-night' ? (
                <select 
                  value={takeawayNight} 
                  onChange={(e) => setTakeawayNight(e.target.value)}
                  style={{ padding: '8px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '0.9rem', width: '200px' }}
                >
                  <option value="">Select a day</option>
                  <option value="Monday">Monday</option>
                  <option value="Tuesday">Tuesday</option>
                  <option value="Wednesday">Wednesday</option>
                  <option value="Thursday">Thursday</option>
                  <option value="Friday">Friday</option>
                  <option value="Saturday">Saturday</option>
                  <option value="Sunday">Sunday</option>
                </select>
              ) : (
                <TagContainer>
                  {takeawayNight ? (
                    takeawayNight.includes(',') ? (
                      takeawayNight.split(',').map((day, index) => (
                        <Tag key={index} selectedGradient={selectedGradient}>{day.trim()}</Tag>
                      ))
                    ) : (
                      <Tag selectedGradient={selectedGradient}>{takeawayNight}</Tag>
                    )
                  ) : (
                    <EmptyState>No takeaway night specified</EmptyState>
                  )}
                </TagContainer>
              )}
            </PreferenceSubSection>

            {(tasteTestData.cookingHabits || editingSection === 'cooking-habits') && (
              <PreferenceSubSection>
                <SectionHeader>
                  <SubHeader>Cooking Habits</SubHeader>
                  <PreferenceEditButton 
                    isVisible={isEditing}
                    isEditing={editingSection === 'cooking-habits'}
                    onClick={() => handleEditSection('cooking-habits')}
                  >
                    {editingSection === 'cooking-habits' ? (
                      <span style={{ fontSize: '18px', color: '#666', lineHeight: 1 }}>×</span>
                    ) : (
                      <PreferenceEditIconImage src="/images/EditICON.png" alt="Edit" />
                    )}
                  </PreferenceEditButton>
                </SectionHeader>
                <div>
                  <DetailRow>
                    <DetailLabel>Cooking Frequency:</DetailLabel>
                    <DetailContent>
                      {editingSection === 'cooking-habits' ? (
                        <CompactSelect 
                          value={tasteTestData.cookingHabits?.frequency || ''} 
                          onChange={(e) => updateCookingFrequency(e.target.value)}
                        >
                          <option value="">Select frequency</option>
                          <option value="daily">Daily</option>
                          <option value="few-times-week">A few times a week</option>
                          <option value="weekly">Weekly</option>
                          <option value="occasionally">Occasionally</option>
                          <option value="rarely">Rarely</option>
                        </CompactSelect>
                      ) : tasteTestData.cookingHabits?.frequency ? (
                        <Tag selectedGradient={selectedGradient}>
                          {tasteTestData.cookingHabits.frequency.charAt(0).toUpperCase() + tasteTestData.cookingHabits.frequency.slice(1).replace('-', ' ')}
                        </Tag>
                      ) : (
                        <EmptyState>Not specified</EmptyState>
                      )}
                    </DetailContent>
                  </DetailRow>
                  
                  <DetailRow>
                    <DetailLabel>Skill Level:</DetailLabel>
                    <DetailContent>
                      {editingSection === 'cooking-habits' ? (
                        <CompactSelect 
                          value={tasteTestData.cookingHabits?.cookingSkillLevel || ''} 
                          onChange={(e) => updateCookingSkillLevel(e.target.value)}
                        >
                          <option value="">Select skill level</option>
                          <option value="beginner">Beginner</option>
                          <option value="intermediate">Intermediate</option>
                          <option value="advanced">Advanced</option>
                          <option value="expert">Expert</option>
                        </CompactSelect>
                      ) : tasteTestData.cookingHabits?.cookingSkillLevel ? (
                        <Tag selectedGradient={selectedGradient}>
                          {tasteTestData.cookingHabits.cookingSkillLevel.charAt(0).toUpperCase() + tasteTestData.cookingHabits.cookingSkillLevel.slice(1)}
                        </Tag>
                      ) : (
                        <EmptyState>Not specified</EmptyState>
                      )}
                    </DetailContent>
                  </DetailRow>
                  
                  <DetailRow>
                    <DetailLabel>Time Available:</DetailLabel>
                    <DetailContent>
                      {editingSection === 'cooking-habits' ? (
                        <CompactSelect 
                          value={tasteTestData.cookingHabits?.timeAvailable || ''} 
                          onChange={(e) => updateTimeAvailable(e.target.value)}
                        >
                          <option value="">Select time</option>
                          <option value="15-minutes">15 minutes or less</option>
                          <option value="30-minutes">30 minutes</option>
                          <option value="1-hour">1 hour</option>
                          <option value="2-hours">2+ hours</option>
                          <option value="varies">It varies</option>
                        </CompactSelect>
                      ) : tasteTestData.cookingHabits?.timeAvailable ? (
                        <Tag selectedGradient={selectedGradient}>
                          {tasteTestData.cookingHabits.timeAvailable.replace('-', ' ')}
                        </Tag>
                      ) : (
                        <EmptyState>Not specified</EmptyState>
                      )}
                    </DetailContent>
                  </DetailRow>
                  
                  <DetailRow style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                    <DetailLabel>Preferred Meal Types:</DetailLabel>
                    {editingSection === 'cooking-habits' && (
                      <Input
                        placeholder="Add meal type and press Enter"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            addPreferredMealType((e.target as HTMLInputElement).value);
                            (e.target as HTMLInputElement).value = '';
                          }
                        }}
                      />
                    )}
                    <TagContainer>
                      {tasteTestData.cookingHabits?.preferredMealTypes && tasteTestData.cookingHabits.preferredMealTypes.length > 0 ? (
                        tasteTestData.cookingHabits.preferredMealTypes.map((mealType, index) => (
                          <Tag key={index} selectedGradient={selectedGradient}>
                            {mealType}
                            {editingSection === 'cooking-habits' && (
                              <button onClick={() => removePreferredMealType(mealType)}>×</button>
                            )}
                          </Tag>
                        ))
                      ) : editingSection !== 'cooking-habits' ? (
                        <EmptyState>No meal types specified</EmptyState>
                      ) : null}
                    </TagContainer>
                  </DetailRow>
                </div>
              </PreferenceSubSection>
            )}

            {(tasteTestData.budgetRange || (tasteTestData.shoppingPreferences && tasteTestData.shoppingPreferences.length > 0) || editingSection === 'budget-shopping') && (
              <PreferenceSubSection>
                <SectionHeader>
                  <SubHeader>Food Budget & Shopping</SubHeader>
                  <PreferenceEditButton 
                    isVisible={isEditing}
                    isEditing={editingSection === 'budget-shopping'}
                    onClick={() => handleEditSection('budget-shopping')}
                  >
                    {editingSection === 'budget-shopping' ? (
                      <span style={{ fontSize: '18px', color: '#666', lineHeight: 1 }}>×</span>
                    ) : (
                      <PreferenceEditIconImage src="/images/EditICON.png" alt="Edit" />
                    )}
                  </PreferenceEditButton>
                </SectionHeader>
                <div>
                  <DetailRow>
                    <DetailLabel>Weekly Budget:</DetailLabel>
                    <DetailContent>
                      {editingSection === 'budget-shopping' ? (
                        <Input
                          type="number"
                          placeholder="Enter budget"
                          value={tasteTestData.budgetRange || ''}
                          onChange={(e) => updateBudgetRange(e.target.value)}
                          style={{ width: '120px', padding: '4px 8px', fontSize: '0.85rem' }}
                          min="0"
                          step="10"
                        />
                      ) : tasteTestData.budgetRange ? (
                        <Tag selectedGradient={selectedGradient}>
                          ${tasteTestData.budgetRange}
                        </Tag>
                      ) : (
                        <EmptyState>Not specified</EmptyState>
                      )}
                    </DetailContent>
                  </DetailRow>
                  
                  <DetailRow style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                    <DetailLabel>Shopping Preferences:</DetailLabel>
                    {editingSection === 'budget-shopping' && (
                      <Input
                        placeholder="Add shopping preference and press Enter"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            addShoppingPreference((e.target as HTMLInputElement).value);
                            (e.target as HTMLInputElement).value = '';
                          }
                        }}
                      />
                    )}
                    <TagContainer>
                      {tasteTestData.shoppingPreferences && tasteTestData.shoppingPreferences.length > 0 ? (
                        tasteTestData.shoppingPreferences.map((preference, index) => (
                          <Tag key={index} selectedGradient={selectedGradient}>
                            {preference}
                            {editingSection === 'budget-shopping' && (
                              <button onClick={() => removeShoppingPreference(preference)}>×</button>
                            )}
                          </Tag>
                        ))
                      ) : editingSection !== 'budget-shopping' ? (
                        <EmptyState>No shopping preferences specified</EmptyState>
                      ) : null}
                    </TagContainer>
                  </DetailRow>
                </div>
              </PreferenceSubSection>
            )}

            {/* CLEANING & CHORE SECTION */}
            {(messTestData.choreAssignments && messTestData.choreAssignments.length > 0) || editingSection === 'chore-assignments' ? (
              <PreferenceSubSection>
                <SectionHeader>
                  <SubHeader>Chore Assignments</SubHeader>
                  <PreferenceEditButton 
                    isVisible={isEditing}
                    isEditing={editingSection === 'chore-assignments'}
                    onClick={() => handleEditSection('chore-assignments')}
                  >
                    {editingSection === 'chore-assignments' ? (
                      <span style={{ fontSize: '18px', color: '#666', lineHeight: 1 }}>×</span>
                    ) : (
                      <PreferenceEditIconImage src="/images/EditICON.png" alt="Edit" />
                    )}
                  </PreferenceEditButton>
                </SectionHeader>
                <div>
                  {editingSection === 'chore-assignments' ? (
                    // Show member selection first, then chores for selected member
                    <div>
                      <div style={{ marginBottom: '16px' }}>
                        <TagContainer>
                          {householdMembers.map((member, memberIndex) => {
                            const memberColor = householdMemberColors[memberIndex];
                            const isSelected = selectedHouseholdMember === memberIndex;
                            
                            return (
                              <HouseholdTag 
                                key={memberIndex}
                                customColor={memberColor}
                                isSelected={isSelected}
                                onClick={() => setSelectedHouseholdMember(memberIndex)}
                                style={{ cursor: 'pointer' }}
                              >
                                {member.name}
                              </HouseholdTag>
                            );
                          })}
                        </TagContainer>
                      </div>
                      
                      <div style={{ minHeight: '140px' }}>
                        {householdMembers.length === 0 ? (
                          <div style={{ 
                            padding: '20px', 
                            textAlign: 'center', 
                            color: '#666',
                            fontStyle: 'italic'
                          }}>
                            Please add household members first to assign chores
                          </div>
                        ) : selectedHouseholdMember !== null ? (
                          <div>
                            <div style={{ 
                               display: 'grid', 
                               gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', 
                               gap: '6px',
                               alignContent: 'start',
                               position: 'relative'
                             }}>
                              {COMMON_CHORES.map((chore) => {
                                const selectedMemberName = householdMembers[selectedHouseholdMember]?.name;
                                const memberAssignment = messTestData.choreAssignments.find(
                                  assignment => assignment.member === selectedMemberName
                                );
                                const memberChores = memberAssignment?.chores || [];
                                const isSelected = memberChores.includes(chore);
                                
                                return (
                                  <span
                                    key={`${selectedMemberName}-${chore}`}
                                    data-chore-tag="true"
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      background: selectedGradient ? selectedGradient : `${theme.colors.primaryLight}40`,
                                      opacity: isSelected ? (selectedGradient ? '0.8' : '1') : '0.5',
                                      color: 'white',
                                      padding: '2px 6px',
                                      borderRadius: '12px',
                                      fontSize: '0.65rem',
                                      fontWeight: '500',
                                      border: `1px solid ${selectedGradient ? 'transparent' : `${theme.colors.primaryLight}60`}`,
                                      cursor: 'pointer',
                                      userSelect: 'none',
                                      WebkitUserSelect: 'none',
                                      MozUserSelect: 'none',
                                      msUserSelect: 'none',
                                      WebkitTouchCallout: 'none',
                                      WebkitTapHighlightColor: 'transparent',
                                      transition: 'all 0.2s ease',
                                      willChange: 'opacity, transform',
                                      transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                                      boxShadow: isSelected ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'
                                    }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      console.log('Chore clicked:', chore, 'for member:', selectedMemberName);
                                      if (selectedMemberName) {
                                        toggleChoreForMember(selectedMemberName, chore);
                                      }
                                    }}
                                    onMouseDown={(e) => e.preventDefault()}
                                  >
                                    {chore}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <div style={{ 
                            padding: '20px', 
                            textAlign: 'center', 
                            color: '#666',
                            fontStyle: 'italic'
                          }}>
                            Select a household member above to assign chores
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    // Show read-only view when not editing
                    messTestData.choreAssignments && messTestData.choreAssignments.length > 0 ? (
                      messTestData.choreAssignments.map((assignment, index) => {
                        // Find the member's color
                        const memberIndex = householdMembers.findIndex(member => member.name === assignment.member);
                        const memberColor = memberIndex >= 0 ? householdMemberColors[memberIndex] : undefined;
                        
                        return assignment.chores.length > 0 ? (
                          <DetailRow key={index} style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                            <HouseholdTag customColor={memberColor}>
                              {assignment.member}
                            </HouseholdTag>
                            <TagContainer>
                              {assignment.chores.map((chore, choreIndex) => (
                                <Tag key={choreIndex} selectedGradient={selectedGradient}>
                                  {chore}
                                </Tag>
                              ))}
                            </TagContainer>
                          </DetailRow>
                        ) : null;
                      })
                    ) : (
                      <EmptyState>No chore assignments specified</EmptyState>
                    )
                  )}
                </div>
              </PreferenceSubSection>
            ) : null}

            {(messTestData.chorePreferences || editingSection === 'chore-preferences') && (
              <PreferenceSubSection>
                <SectionHeader>
                  <SubHeader>Chore Preferences</SubHeader>
                  <PreferenceEditButton 
                    isVisible={isEditing}
                    isEditing={editingSection === 'chore-preferences'}
                    onClick={() => handleEditSection('chore-preferences')}
                  >
                    {editingSection === 'chore-preferences' ? (
                      <span style={{ fontSize: '18px', color: '#666', lineHeight: 1 }}>×</span>
                    ) : (
                      <PreferenceEditIconImage src="/images/EditICON.png" alt="Edit" />
                    )}
                  </PreferenceEditButton>
                </SectionHeader>
                <div>
                  <DetailRow>
                    <DetailLabel>Preferred Time:</DetailLabel>
                    <DetailContent>
                      {messTestData.chorePreferences?.preferredTime ? (
                        <Tag selectedGradient={selectedGradient}>
                          {messTestData.chorePreferences.preferredTime.charAt(0).toUpperCase() + messTestData.chorePreferences.preferredTime.slice(1)}
                        </Tag>
                      ) : (
                        <EmptyState>Not specified</EmptyState>
                      )}
                    </DetailContent>
                  </DetailRow>
                  
                  <DetailRow>
                    <DetailLabel>Motivation Style:</DetailLabel>
                    <DetailContent>
                      {messTestData.chorePreferences?.motivationStyle ? (
                        <Tag selectedGradient={selectedGradient}>
                          {messTestData.chorePreferences.motivationStyle.charAt(0).toUpperCase() + messTestData.chorePreferences.motivationStyle.slice(1)}
                        </Tag>
                      ) : (
                        <EmptyState>Not specified</EmptyState>
                      )}
                    </DetailContent>
                  </DetailRow>
                  
                  <DetailRow>
                    <DetailLabel>Cleaning Style:</DetailLabel>
                    <DetailContent>
                      {messTestData.chorePreferences?.choreStyle ? (
                        <Tag selectedGradient={selectedGradient}>
                          {messTestData.chorePreferences.choreStyle.replace('-', ' ').charAt(0).toUpperCase() + messTestData.chorePreferences.choreStyle.replace('-', ' ').slice(1)}
                        </Tag>
                      ) : (
                        <EmptyState>Not specified</EmptyState>
                      )}
                    </DetailContent>
                  </DetailRow>
                  
                  <DetailRow>
                    <DetailLabel>Step-by-step guidance:</DetailLabel>
                    <DetailContent>
                      <Tag selectedGradient={selectedGradient}>
                        {messTestData.stepByStepPreference ? 'Yes, please!' : 'Keep it simple'}
                      </Tag>
                    </DetailContent>
                  </DetailRow>
                </div>
              </PreferenceSubSection>
            )}

            {(messTestData.productiveDays && messTestData.productiveDays.length > 0) || editingSection === 'productive-days' ? (
              <PreferenceSubSection>
                <SectionHeader>
                  <SubHeader>Most Productive Days</SubHeader>
                  <PreferenceEditButton 
                    isVisible={isEditing}
                    isEditing={editingSection === 'productive-days'}
                    onClick={() => handleEditSection('productive-days')}
                  >
                    {editingSection === 'productive-days' ? (
                      <span style={{ fontSize: '18px', color: '#666', lineHeight: 1 }}>×</span>
                    ) : (
                      <PreferenceEditIconImage src="/images/EditICON.png" alt="Edit" />
                    )}
                  </PreferenceEditButton>
                </SectionHeader>
                <TagContainer>
                  {editingSection === 'productive-days' ? (
                    // Show all days as clickable options when editing
                    ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                      <Tag 
                        key={day} 
                        selectedGradient={selectedGradient}
                        style={{ 
                          cursor: 'pointer',
                          opacity: messTestData.productiveDays.includes(day) ? 1 : 0.5
                        }}
                        onClick={() => toggleProductiveDay(day)}
                      >
                        {day}
                      </Tag>
                    ))
                  ) : (
                    // Show selected days when not editing
                    messTestData.productiveDays && messTestData.productiveDays.length > 0 ? (
                      messTestData.productiveDays.map((day, index) => (
                        <Tag key={index} selectedGradient={selectedGradient}>
                          {day}
                        </Tag>
                      ))
                    ) : (
                      <EmptyState>No productive days specified</EmptyState>
                    )
                  )}
                </TagContainer>
              </PreferenceSubSection>
            ) : null}

            {(messTestData.noChoresDays && messTestData.noChoresDays.length > 0) || editingSection === 'no-chores-days' ? (
              <PreferenceSubSection>
                <SectionHeader>
                  <SubHeader>No Chores Days</SubHeader>
                  <PreferenceEditButton 
                    isVisible={isEditing}
                    isEditing={editingSection === 'no-chores-days'}
                    onClick={() => handleEditSection('no-chores-days')}
                  >
                    {editingSection === 'no-chores-days' ? (
                      <span style={{ fontSize: '18px', color: '#666', lineHeight: 1 }}>×</span>
                    ) : (
                      <PreferenceEditIconImage src="/images/EditICON.png" alt="Edit" />
                    )}
                  </PreferenceEditButton>
                </SectionHeader>
                <TagContainer>
                  {editingSection === 'no-chores-days' ? (
                    // Show all days as clickable options when editing
                    ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                      <Tag 
                        key={day} 
                        selectedGradient={selectedGradient}
                        style={{ 
                          cursor: 'pointer',
                          opacity: messTestData.noChoresDays.includes(day) ? 1 : 0.5
                        }}
                        onClick={() => toggleNoChoreDay(day)}
                      >
                        {day}
                      </Tag>
                    ))
                  ) : (
                    // Show selected days when not editing
                    messTestData.noChoresDays && messTestData.noChoresDays.length > 0 ? (
                      messTestData.noChoresDays.map((day, index) => (
                        <Tag key={index} selectedGradient={selectedGradient}>
                          {day}
                        </Tag>
                      ))
                    ) : (
                      <EmptyState>No rest days specified</EmptyState>
                    )
                  )}
                </TagContainer>
              </PreferenceSubSection>
            ) : null}
          </PreferencesSection>
        </CenterSection>
      </ProfileCard>
      
      <NestTestModal
        isOpen={isNestTestModalOpen}
        onClose={() => setIsNestTestModalOpen(false)}
        onSubmit={handleNestTestSubmit}
        initialHouseholdMembers={householdMembers}
      />
      
      <TasteTestModal
        isOpen={isTasteTestModalOpen}
        onClose={() => setIsTasteTestModalOpen(false)}
        onSubmit={handleTasteTestSubmit}
        initialData={tasteTestData}
      />
      
      <MessTestModal
        isOpen={isMessTestModalOpen}
        onClose={() => setIsMessTestModalOpen(false)}
        onSubmit={handleMessTestSubmit}
        householdMembers={householdMembers}
        initialData={messTestData}
      />
    </ProfileContainer>
  );
};

export default Profile; 